// The one mutable source of truth. A module-level store with subscribe/version,
// read through `useSyncExternalStore` — so non-React callers (the SDK bridge,
// the persistence timer) and React see exactly the same state, and StrictMode's
// double-mount is harmless.

import { Engine } from '@/engine/engine';
import { naturalCompare } from '@/model/collate';
import type {
  Border,
  BorderEdge,
  CellStyle,
  ChartSpec,
  FormatId,
  Merge,
  Sheet,
  Workbook,
} from '@/model/types';
import {
  activeSheet,
  addSheet,
  cellRawText,
  clearCells,
  createWorkbook,
  getCell,
  newId,
  setCellFormat,
  setCellRaw,
  setCellValue,
  sheetByRole,
  sheetById,
  uniqueSheetName,
  isSystemSheet,
  normalizeSystemSheetOrder,
  systemBlockStart,
  parseCk,
  colWidth,
  rowHeight,
} from '@/model/workbook';
import { toggleReferenceAt, translateFormula } from '@/model/address';
import type { McpStatus } from '@/giraffe/mcpBridge';
import { applyPick, canPointAt } from '@/model/pointing';
import { formatValue } from '@/engine/format';
import { parseTsv, toTsv } from '@/grid/clipboard';
import { parseHtmlTable } from '@/grid/htmlClipboard';
import { detectSeries, type Series, type SeriesCell } from '@/model/series';
import { edgesFor, type BorderTarget } from '@/ui/borders';
import { applyStructural, deleteBand, insertBand, moveBand, sortMap } from '@/model/structure';
import { growToMerges, isCoveredNonAnchor, mergeAt, overlapsAny } from '@/model/merge';
import { measureText, wrappedLines } from '@/grid/measure';
import {
  autoPushable,
  ensureUsagesSheet,
  fullDiff,
  pushUsages,
  writeUsages,
  type PushResult,
  type UsageDiff,
  fieldsInColumns,
  pushFieldRemoval,
  usageColumns,
} from '@/giraffe/usagesSheet';
import { FIRST_DATA_ROW, HEADER_ROW, dropDuplicateColumns, restoreArrangement, reunitTable, toProjectUnits } from '@/data/entityTable';
import { repairImportedFormulas, type XlsxImport } from '@/data/xlsxImport';
import { renameSheetRefs } from '@/data/xlsxExport';
import {
  dropHiddenColumns,
  ensureFeaturesSheet,
  featureColumns,
  featureDiff,
  featureIds,
  hiddenFromSheet,
  writeFeatures,
  SHOW_FIELD,
} from '@/data/featureTable';
import {
  areaColumns,
  areaIds,
  dropHiddenAreaColumns,
  ensureAreasSheet,
  gtablesExpressionColumns,
  hiddenFromAreasSheet,
  writeAreas,
  type CustomCalcColumn,
} from '@/data/areasTable';
import {
  blockColumns,
  blockLabel,
  blockRows,
  blockValues,
  countInstances,
  ensureBlocksSheet,
  writeBlocks,
} from '@/data/blockTable';
import { ensureAnalyticsSheet, hasAnalyticsMeasures, writeAnalyticsSheet } from '@/data/analyticsTable';
import { getAnalyticsResult } from '@/giraffe/analyticsResult';
import { readAllMeasures, readAnalyticsLayout } from '@/giraffe/analyticsMeasures';
import { resolveNativeMeasures, isResolved, sortByAnalyticsOrder } from '@/giraffe/nativeMeasures';
import { fieldsInColumns as fieldsInColumnsOf, type EntityRow } from '@/data/entityTable';
import {
  fitBounds,
  flyTo,
  releaseRender,
  setHiddenFeatures,
  setSelectedFeatures,
  writeBlockProps,
  writeFeatureProps,
  type BlockDef,
} from '@/giraffe/adapter';
import { boundsOf } from '@/giraffe/normalize';
import { listAt, listForCell, resolveList, rowListAt, suggestOptions } from '@/model/dropdowns';
import { distinct } from '@/data/aggregate';
import type { ColumnList, FieldBinding } from '@/model/types';
import {
  FIRST_FILTERABLE_ROW,
  anyFilter,
  filteredRows,
  lastContentRow,
  type ColumnFilter,
} from '@/model/filters';

/** One shared empty set, so "nothing filtered" allocates nothing per render. */
const NO_ROWS: ReadonlySet<number> = new Set();
import type { GiraffeData, MeasureClass, SourceKind } from '@/data/types';
import { EMPTY_DATA } from '@/data/types';
import { overrideKey, withClassOverrides } from '@/data/overrides';
import { headerAliases, withDisplayUnits, type UnitSystem } from '@/data/units';
import { projectUnits as readProjectUnits, resolveSystem, unitsLabel, type ProjectUnits } from '@/giraffe/units';
import { stampSchedule, type ScheduleSpec } from '@/giraffe/stamp';
import { databaseAt, matchesOf, removeDatabase, subRowMembers, writeDatabase } from '@/giraffe/database';
import { layerVisibility, sheetFilterActive, sheetFilterHiddenRows, sheetFilterText, type SheetFilterClause } from '@/model/sheetFilter';
import type { Database } from '@/model/types';

export type { BorderTarget };

export interface FindMatch {
  sheetId: string;
  row: number;
  col: number;
  text: string;
}

export interface Selection {
  sheetId: string;
  /** The cell that receives typing. Always inside the rectangle. */
  row: number;
  col: number;
  /** Rectangle, normalised so r1<=r2 and c1<=c2. */
  r1: number;
  c1: number;
  r2: number;
  c2: number;
  /**
   * Set when the selection was made from a header, so it means "these whole
   * rows" rather than "this rectangle". Inferring it from the rectangle
   * covering the sheet's extent would call a hand-dragged full-height
   * selection a row selection, which is not what the user asked for.
   */
  band?: 'row' | 'col';
}

export interface ClipboardPayload {
  /** Raw cell text — formulas and unformatted literals. The in-app payload. */
  cells: string[][];
  /**
   * What the cells LOOK like. This is what goes on the system clipboard,
   * because Excel and Google Sheets both copy displayed text — paste a raw
   * date serial into Excel and it shows 46025, not a date.
   */
  display: string[][];
  /** Number format per cell — copying a cell copies how it looks, as in Excel. */
  formats: (FormatId | undefined)[][];
  /** Bold, colours, borders. */
  styles: (CellStyle | undefined)[][];
  /** Where it was copied from, for relative-reference translation. */
  origin: { row: number; col: number } | null;
  cut: boolean;
  /** Merges fully inside the copied rect, offset relative to its top-left. */
  merges?: Merge[];
}

interface UndoFrame {
  label: string;
  sheets: string;
  selection: Selection;
  /**
   * The unit system the snapshot's numbers are in.
   *
   * A frame taken before a switch to feet holds metres, and restoring it as-is
   * would put metric values under imperial headers — and leave the entity
   * sheets' baseline disagreeing with the project in a way that reads as
   * unpushed edits. Recorded so `restore` can respell it. See data/units.ts.
   */
  units: UnitSystem;
}

const UNDO_LIMIT = 40;
/** How long typing has to pause before an edit on a project sheet is sent. */
const AUTO_PUSH_DEBOUNCE_MS = 600;

const DEV_MODE_KEY = 'gtables:devMode';
const MCP_KEY = 'gtables:mcp';

export class AppStore {
  wb: Workbook = createWorkbook();
  engine: Engine;
  sel: Selection;
  /**
   * Hides the features being superseded by live-Giraffe-analytics work — the
   * Database wizard (`Toolbar.tsx`), the Units dropdown (`Toolbar.tsx`'s
   * `UnitsSelect`), and the Analytics sheet tab (`SheetTabs.tsx`) — without
   * removing any of them: the Analytics sheet itself keeps existing and
   * updating in the background, it is only the TAB that is hidden, so
   * nothing that reads from it elsewhere (native-measure sorting, say) loses
   * anything by this being off. Off by default, persisted across sessions
   * because it is a preference about this browser's use of the app, not the
   * document. The GI.*EACH family is NOT gated behind this: it is in
   * everyday use (the Measures tab is built on it), not something being
   * phased out.
   */
  devMode: boolean = (() => {
    try {
      return localStorage.getItem(DEV_MODE_KEY) === '1';
    } catch {
      return false;
    }
  })();

  toggleDevMode(): void {
    this.devMode = !this.devMode;
    try {
      localStorage.setItem(DEV_MODE_KEY, this.devMode ? '1' : '0');
    } catch {
      /* no storage available — the toggle still works for this session */
    }
    // Turning dev mode OFF hides the Analytics tab (SheetTabs.tsx) — if it
    // was the one on screen, leaving `activeSheetId` pointed at a sheet with
    // no visible tab anymore is a dead end, not a toggle.
    if (!this.devMode) {
      const active = this.wb.sheets.find((s) => s.id === this.wb.activeSheetId);
      if (active?.role === 'analytics') {
        const fallback = sheetByRole(this.wb, 'areas') ?? sheetByRole(this.wb, 'features') ?? this.wb.sheets[0];
        if (fallback) this.wb.activeSheetId = fallback.id;
      }
    }
    this.emitUiOnly();
  }

  /**
   * Whether this panel offers its tools to Giraffe's MCP relay (see
   * giraffe/mcpBridge.ts). Opt-in and per browser, like Dev Mode: switched on,
   * it holds a socket to the relay open for as long as the panel is.
   */
  mcpEnabled: boolean = (() => {
    try {
      return localStorage.getItem(MCP_KEY) === '1';
    } catch {
      return false;
    }
  })();

  /** The relay connection, for the File menu to show. Null while MCP is off. */
  mcpStatus: McpStatus | null = null;

  toggleMcp(): void {
    this.mcpEnabled = !this.mcpEnabled;
    try {
      localStorage.setItem(MCP_KEY, this.mcpEnabled ? '1' : '0');
    } catch {
      /* no storage available — the toggle still works for this session */
    }
    if (!this.mcpEnabled) this.mcpStatus = null;
    this.emitUiOnly();
  }

  setMcpStatus(status: McpStatus | null): void {
    this.mcpStatus = status;
    this.emitUiOnly();
  }

  /**
   * Write a block of raw cell text (a leading `=` makes a formula) at
   * (row, col) on any ordinary sheet, as ONE undo step and one recalc — the
   * MCP write path. System sheets are refused: they are rewritten from the
   * project on every bake, and the editable ones push straight to Giraffe.
   */
  writeCellsAt(sheetId: string, row: number, col: number, grid: readonly (readonly string[])[], label = 'Edit cells'): number {
    const sheet = sheetById(this.wb, sheetId);
    if (!sheet) throw new Error(`no sheet with id ${sheetId}`);
    if (isSystemSheet(sheet)) throw new Error(`"${sheet.name}" is a system sheet, rebuilt from the project; write to an ordinary sheet instead.`);
    const width = Math.max(0, ...grid.map((r) => r.length));
    if (grid.length === 0 || width === 0) return 0;
    this.pushUndo(label);
    let n = 0;
    grid.forEach((cells, i) =>
      cells.forEach((raw, j) => {
        setCellRaw(sheet, row + i, col + j, raw);
        n += 1;
      })
    );
    this.engine.onRangeEdited(sheet.id, row, col, row + grid.length - 1, col + width - 1);
    this.emit();
    return n;
  }

  /** Add an ordinary sheet without moving the user off the one they are on. */
  addSheetNamed(name: string): Sheet {
    this.pushUndo('Add sheet');
    const sheet = addSheet(this.wb, name);
    this.emit();
    return sheet;
  }

  clipboard: ClipboardPayload | null = null;
  /**
   * The open cell editor. The TEXT lives here, not in the editor component,
   * because pointing at a cell to insert its reference is a conversation
   * between the grid and the editor — the grid has to be able to rewrite the
   * formula being typed.
   */
  editing: {
    /**
     * The sheet the edited cell lives on. The editor outlives a sheet switch:
     * building `=Sheet2!A1` means leaving the sheet you are typing on, and the
     * formula has to come back to the cell it started in.
     */
    sheetId: string;
    row: number;
    col: number;
    text: string;
    caret: number;
    /**
     * Set while the user is picking a reference by pointing. `start` is where
     * the inserted reference begins in `text`, so moving the pick rewrites it
     * in place instead of appending a trail of addresses.
     */
    pointing: { start: number; r1: number; c1: number; r2: number; c2: number; sheetId: string } | null;
  } | null = null;
  /** Non-null while the app is talking to Giraffe. */
  private dataProvider: GiraffeData = EMPTY_DATA;
  /** What each live block last rendered — overflow counts, warnings, errors. */
  /** Edits staged against a writable block column, awaiting an explicit push. */

  /**
   * Bumped when the grid should take the keyboard.
   *
   * The grid's own focus effect refuses to steal focus from a control the user
   * deliberately clicked — which is right for the toolbar and the side panes,
   * and wrong for the one case where clicking a button IS a request to go to
   * the sheet: adding a sheet leaves focus on the `+`, so typing went nowhere.
   * An explicit request is unambiguous where a guess cannot be.
   */
  gridFocusToken = 0;

  focusGrid(): void {
    // Never while a formula is open: the editor holds the caret, and taking it
    // would blur the input, and blur commits.
    if (this.editing) return;
    this.gridFocusToken += 1;
    this.emit();
  }

  private wrapped: GiraffeData | null = null;
  private wrappedFor: GiraffeData | null = null;
  private wrappedOverrides: Readonly<Record<string, MeasureClass>> | null = null;
  private wrappedUnits: UnitSystem | null = null;

  private listeners = new Set<() => void>();
  private snapshotVersion = 0;
  private undoStack: UndoFrame[] = [];
  private redoStack: UndoFrame[] = [];

  constructor() {
    this.engine = new Engine({
      workbook: this.wb,
      data: () => this.data(),
      onMeasureExpressionsChanged: (expressions) => this.onMeasureExpressionsChanged(expressions),
    });
    const first = this.wb.sheets[0]!;
    this.sel = { sheetId: first.id, row: 1, col: 1, r1: 1, c1: 1, r2: 1, c2: 1 };
  }

  // -- React glue -----------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): number => this.snapshotVersion;

  /**
   * Whether the emit now being delivered was caused by the PROJECT rather than
   * by the person using the app.
   *
   * Persistence needs the difference. `ensureSystemSheets` rewrites the system
   * sheets whenever Giraffe broadcasts, and that legitimately changes the saved
   * document — the column list on the Features sheet goes from `["Feature"]` to
   * every field the project has, seconds after load. Worth saving; not the
   * user's unsaved work, and showing it as a red dot on a sheet nobody has
   * touched is how "Unsaved" stops meaning anything.
   */
  /**
   * Whether the last emit was something OTHER than the user changing the
   * document — the project rewriting a system sheet, or a click that moved the
   * selection. Persistence reads it to decide whether there is unsaved WORK, as
   * opposed to something merely having happened.
   */
  lastEmitWasIncidental = false;
  private incidental = false;

  private emit(): void {
    this.lastEmitWasIncidental = this.incidental;
    this.snapshotVersion += 1;
    for (const l of this.listeners) l();
  }

  /**
   * Run `fn` and emit, with everything it changes attributed to the project.
   *
   * Called with no `fn` for a change that is not the document at all — a status
   * chip appearing or fading. Either way the persistence layer must not read it
   * as the user having unsaved work.
   */
  private emitAsData(fn?: () => void): void {
    this.incidental = true;
    try {
      fn?.();
      this.emit();
    } finally {
      this.incidental = false;
    }
  }

  /**
   * An emit that moved the CURSOR, not the document.
   *
   * Selecting a cell, extending a range, moving the caret in an open editor and
   * typing before Enter all redraw the screen and change nothing that is saved.
   * Counting them as unsaved work put "Unsaved changes" on a workbook a second
   * after it opened, on nothing more than a click — and left the Save button
   * lit with nothing to send.
   */
  private emitUiOnly(): void {
    this.incidental = true;
    try {
      this.emit();
    } finally {
      this.incidental = false;
    }
  }

  // -- Accessors ------------------------------------------------------------

  sheet(): Sheet {
    return sheetById(this.wb, this.sel.sheetId) ?? activeSheet(this.wb);
  }

  /**
   * Whether a cell is locked against editing — checked BEFORE every
   * value-changing action (typing, paste, fill, clear), not after. The
   * earlier "read-only" guarantee on Areas only stopped an edit from
   * surviving the next rewrite (`writeAreas`'s `preserveEdits` removed
   * entirely — see its doc comment) — nothing stopped it from being typed
   * in the first place, which read as "not actually read-only" even though
   * nothing it did ever reached the project. This is the other half: typing
   * into a locked cell does nothing visible at all.
   *
   * Areas only, and not its own Show switch — that is gTables' own toggle,
   * not project data (see `afterValueEdit`), and stays live.
   *
   * Public, not private: `Grid.tsx` reads this too, to stop a locked cell's
   * usage/layerId chip from opening its dropdown at all on click — a chip
   * that opens a menu and then silently refuses the pick still LOOKS
   * editable, which is its own version of the same gap this guard was
   * written to close.
   */
  isCellLocked(sheet: Sheet, col: number): boolean {
    if (sheet.role !== 'areas') return false;
    return getCell(sheet, HEADER_ROW, col)?.v !== 'Show';
  }

  /**
   * Whether EVERY cell on this sheet is locked — right now, only Areas
   * (the Show column is the one exception `isCellLocked` carves out per
   * cell, not per sheet, so this is not just `isCellLocked` for column 1).
   *
   * `FormatMenu.tsx` reads this to hide Style and Clear on a sheet where
   * neither can do anything that survives the next rewrite — the same
   * "looks editable, isn't" gap `isCellLocked` itself exists to close,
   * one level up: an item that opens a page of controls which then quietly
   * do nothing is worse than no item at all.
   */
  isReadOnlySheet(sheet: Sheet = this.sheet()): boolean {
    return sheet.role === 'areas';
  }

  activeRawText(): string {
    return cellRawText(getCell(this.sheet(), this.sel.row, this.sel.col));
  }

  /**
   * The provider everything reads through — the live one, wrapped so the
   * workbook's measure-class overrides win over the app's inference.
   *
   * Cached against the provider's version and the override set, because it is
   * called from every GI.* evaluation and rebuilding the wrapper per call would
   * defeat the caching inside the provider itself.
   */
  data(): GiraffeData {
    const overrides = this.wb.fieldClasses ?? EMPTY_OVERRIDES;
    const units = this.units();
    if (
      this.wrapped &&
      this.wrappedFor === this.dataProvider &&
      this.wrappedOverrides === overrides &&
      this.wrappedUnits === units
    ) {
      return this.wrapped;
    }
    // Units OUTSIDE the class overrides, so `units()` survives to the readers
    // and the conversion sees the same rows the overrides do. Metric is a
    // no-op wrapper — see withDisplayUnits.
    this.wrapped = withDisplayUnits(withClassOverrides(this.dataProvider, overrides), units);
    this.wrappedFor = this.dataProvider;
    this.wrappedOverrides = overrides;
    this.wrappedUnits = units;
    return this.wrapped;
  }

  // -- Units ----------------------------------------------------------------

  /**
   * What the PROJECT says it measures in. Read off the `project` state key by
   * the bridge (giraffe/units.ts); null until it arrives, or when it says
   * nothing this app recognises.
   */
  private projectUnitSetting: ProjectUnits | null = null;

  /**
   * The system every number in the app is in: the workbook's override, else the
   * project's setting, else metric.
   *
   * On the WORKBOOK rather than in localStorage, because a stamped schedule
   * writes its unit into a header CELL — "Gross Area (ft²)" is ordinary text
   * that gets saved and shared. Two people opening the same sheet in different
   * systems would be reading those headers against numbers in the other one.
   */
  units(): UnitSystem {
    return resolveSystem(this.wb.units, this.projectUnitSetting);
  }

  /** The workbook's own choice, or undefined when it follows the project. */
  unitOverride(): UnitSystem | undefined {
    return this.wb.units;
  }

  /** "ft / sqft", for saying what the project asked for even when unrecognised. */
  projectUnitsLabel(): string {
    return unitsLabel(this.projectUnitSetting);
  }

  /** What the project setting resolves to on its own, override aside. */
  projectSystem(): UnitSystem {
    return resolveSystem(undefined, this.projectUnitSetting);
  }

  /** The bridge, on every `project` broadcast. Cheap when nothing changed. */
  setProjectUnits(project: unknown): void {
    const next = readProjectUnits(project);
    const cur = this.projectUnitSetting;
    if (cur?.length === next?.length && cur?.area === next?.area) return;
    const before = this.units();
    this.projectUnitSetting = next;
    const after = this.units();
    if (after === before) return;
    // The project's doing, not the user's — this must not read as unsaved work.
    this.emitAsData(() => this.reunit(before, after));
  }

  /**
   * Override the project's setting, or clear the override to follow it again.
   *
   * No undo frame: the control IS the way back, it is sitting in the toolbar,
   * and nothing is thrown away — the numbers are respelled, not replaced. An
   * undo step that only restored the cells and left the setting where it was
   * would be an inert entry in the stack.
   */
  setUnitOverride(override: UnitSystem | undefined): void {
    if (this.wb.units === override) return;
    const before = this.units();
    if (override === undefined) delete this.wb.units;
    else this.wb.units = override;
    const after = this.units();
    if (after !== before) this.reunit(before, after);
    this.emit();
  }

  /**
   * Respell everything for a change of system.
   *
   * The entity sheets have to be converted IN PLACE before they are rewritten:
   * they carry a baseline of what the project last said, and rewriting against
   * a baseline still in the old units makes every untouched cell look like an
   * unpushed edit. See `reunitTable`.
   *
   * Formulas need nothing — GI.* calls are volatile, so `onDataChanged` is
   * enough to bring every stamped schedule over. What a stamped schedule does
   * NOT change is its header text, which was written when it was stamped: it
   * says the units of the day it was made, and re-labelling cells the user may
   * since have edited would be this app reaching into their sheet.
   */
  private reunit(from: UnitSystem, to: UnitSystem): void {
    this.wrapped = null;
    this.reunitSheets(from, to);
    this.ensureSystemSheets();
    this.engine.onDataChanged();
  }

  /**
   * The entity sheets, respelled in place — cells and remembered baseline
   * together.
   *
   * Blocks are not converted at all: their columns are the user's own, and
   * nothing about them is a project measure. See blockTable.ts.
   */
  private reunitSheets(from: UnitSystem, to: UnitSystem): void {
    if (from === to) return;
    const data = this.data();
    const usages = sheetByRole(this.wb, 'usages');
    if (usages) reunitTable(usages, usageColumns(data), from, to);
    const features = sheetByRole(this.wb, 'features');
    if (features) reunitTable(features, featureColumns(data, this.hiddenFeatures), from, to);
    const areas = sheetByRole(this.wb, 'areas');
    if (areas) reunitTable(areas, areaColumns(data, this.hiddenFeatures), from, to);
  }

  /** What the app worked out on its own, before any override. */
  inferredClassOf(source: SourceKind, field: string): MeasureClass {
    return this.dataProvider.inferredClassOf?.(source, field) ?? this.dataProvider.classOf(source, field);
  }

  classOverride(source: SourceKind, field: string): MeasureClass | undefined {
    return this.wb.fieldClasses?.[overrideKey(source, field)];
  }

  /**
   * Correct the app's guess. Passing the inferred class back clears the
   * override rather than pinning it — so a field only carries one when it
   * actually disagrees with the inference.
   */
  setClassOverride(source: SourceKind, field: string, cls: MeasureClass | null): void {
    const key = overrideKey(source, field);
    const current = this.wb.fieldClasses ?? {};
    const clearing = cls === null || cls === this.inferredClassOf(source, field);
    if (clearing && current[key] === undefined) return;
    if (!clearing && current[key] === cls) return;

    this.pushUndo('Measure class');
    const next = { ...current };
    if (clearing) delete next[key];
    else next[key] = cls;
    if (Object.keys(next).length > 0) this.wb.fieldClasses = next;
    else delete this.wb.fieldClasses;

    this.wrapped = null;
    this.engine.onDataChanged();
    this.emit();
  }

  // -- Giraffe --------------------------------------------------------------

  setDataProvider(provider: GiraffeData): void {
    this.emitAsData(() => {
      this.dataProvider = provider;
      this.wrapped = null;
      this.ensureSystemSheets();
      this.engine.onDataChanged();
    });
  }

  /**
   * Re-read the sources that are FETCHED rather than broadcast.
   *
   * `usages` is not a reactive state key — it is pulled with `getProjectUsages`
   * — so nothing tells the app that a usage it just deleted is gone. Without
   * this the sheet is rewritten from the stale list and the deleted row comes
   * straight back, looking as though the delete failed when it had worked.
   */
  private refetch: (() => Promise<void>) | null = null;

  setRefetcher(fn: (() => Promise<void>) | null): void {
    this.refetch = fn;
  }

  /**
   * Whether a model-driven rewrite (`notifyDataChanged`) is in flight — a
   * real project's bake can rewrite every system sheet and recompute every
   * formula synchronously, which blocks the main thread for long enough to
   * read as the app having frozen, with nothing on screen saying why. The
   * UI (`App.tsx`) renders a greyed-out overlay with a spinner while this is
   * true, same idea as `save`/`status` already have their own indicators.
   */
  busy = false;

  /** A `notifyDataChanged` rewrite is scheduled and has not run yet. */
  private rebuildQueued = false;

  /**
   * The provider's contents changed in place (a bake, a selection change).
   *
   * The actual rewrite is deferred one macrotask (`setTimeout`, not a
   * promise microtask — a microtask runs before the next paint, which would
   * defeat the point) specifically so the browser gets to PAINT the busy
   * overlay before the synchronous work that blocks it begins. Setting
   * `busy` and calling the heavy work back to back does not work: nothing
   * between them yields to the browser's render loop, so the overlay and
   * the freeze it is meant to explain would appear in the same frame.
   */
  notifyDataChanged(): void {
    // Coalesced: the rewrite reads the provider as it stands when it runs, so
    // a second notice before it has run adds nothing but another full
    // rebuild. On load the bridge fires several in a row (sections, usages,
    // the retry ladder), and each used to queue its own.
    if (this.rebuildQueued) return;
    this.rebuildQueued = true;
    this.busy = true;
    this.emitAsData(); // "a status chip appearing" — see emitAsData's own doc comment
    setTimeout(() => {
      this.rebuildQueued = false;
      this.emitAsData(() => {
        this.wrapped = null;
        this.ensureSystemSheets();
        this.engine.onDataChanged();
      });
      this.busy = false;
      this.emitAsData();
    }, 0);
  }

  /**
   * Only the map selection moved. No system sheet shows the selection, so
   * nothing is rewritten: the GI.* formulas (which can read it) recalc, and
   * that is all. A queued full rebuild already covers this.
   */
  notifySelectionChanged(): void {
    if (this.rebuildQueued) return;
    this.emitAsData(() => {
      this.wrapped = null;
      this.engine.onDataChanged();
    });
  }

  /**
   * The Features sheet's tier-d columns: every gTables `GI.*EACH` expression
   * PLUS every native Giraffe-authored Custom Calculation that resolves to a
   * real per-feature formula — only ones built entirely from plain Property
   * components, not a passthrough of a single field (see
   * `giraffe/nativeMeasures.ts`). Unmappable native measures are silently
   * absent — the same "what cannot be materialised simply waits" rule the
   * gTables side already follows.
   *
   * Deduplicated by header, NORMALISED (whitespace stripped) — two
   * measures that compute the exact same thing collapse to one column
   * regardless of what tells them apart in Giraffe (a gTables expression
   * mirrored into Giraffe's own Custom Calculations by `syncCustomCalculations`
   * would otherwise be read back as a second, identical native measure; and
   * Giraffe itself often carries the SAME formula/components under several
   * names differing only by category or filter — e.g. half a dozen
   * "Construction - <Usage>" measures that are all just `netArea*hardCost`).
   * Whichever is seen first wins; gTables expressions are placed first, so
   * they do.
   */
  private customCalcColumns(expressions: string[]): CustomCalcColumn[] {
    const gtables = gtablesExpressionColumns(expressions);
    if (!this.data().live) return gtables;
    const normalise = (header: string): string => header.replace(/\s+/g, '');
    const seen = new Set(gtables.map((c) => normalise(c.header)));
    const knownFields = new Set(areaColumns(this.data(), this.hiddenFeatures));
    // Analytics' own display order — see nativeMeasures.ts's
    // `sortByAnalyticsOrder` doc comment — rather than alphabetical, so this
    // sheet lists them the way a user already expects from Analytics itself.
    const ordered = sortByAnalyticsOrder(readAllMeasures(), readAnalyticsLayout());
    const native: CustomCalcColumn[] = [];
    let order = 0;
    for (const m of resolveNativeMeasures(ordered, knownFields)) {
      if (!isResolved(m)) continue;
      const key = normalise(m.header);
      if (seen.has(key)) continue;
      seen.add(key);
      native.push({ header: m.header, fields: m.fields, template: m.template, order: order++ });
    }
    return [...gtables, ...native];
  }

  /**
   * The Usages sheet exists in every workbook — created here rather than only
   * at `createWorkbook`, so a workbook saved before it existed, or one of the
   * checked-in examples, gains it on load instead of being the odd one out with
   * no way to reach its usages.
   */
  ensureSystemSheets(): void {
    const data = this.data();
    // Unpushed edits survive a refresh throughout: a bake arriving mid-edit
    // must not throw away what the user typed.
    if (data.rows('usages').length > 0 || this.wb.sheets.some((s) => s.role === 'usages')) {
      const sheet = ensureUsagesSheet(this.wb);
      const restored = restoreArrangement(sheet);
      dropDuplicateColumns(this.wb, sheet);
      writeUsages(sheet, data, true, this.wb);
      if (restored) this.resort(sheet);
      this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    }
    if (data.rows('rawSections').length > 0 || sheetByRole(this.wb, 'areas')) {
      const sheet = ensureAreasSheet(this.wb);
      const restored = restoreArrangement(sheet);
      // Before the write, not after: a leftover id column left standing is a
      // column the new ones get placed to the right of.
      dropHiddenAreaColumns(this.wb, sheet);
      dropDuplicateColumns(this.wb, sheet);
      writeAreas(
        sheet,
        data,
        this.hiddenFeatures,
        this.customCalcColumns(this.engine.currentMeasureExpressions()),
        this.wb
      );
      if (restored) this.resort(sheet);
      this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    }
    if (data.rows('rawSections').length > 0 || sheetByRole(this.wb, 'features')) {
      const sheet = ensureFeaturesSheet(this.wb);
      restoreArrangement(sheet);
      dropHiddenColumns(this.wb, sheet);
      dropDuplicateColumns(this.wb, sheet);
      writeFeatures(sheet, data, this.hiddenFeatures, true, this.wb);
      // NOT `if (restored) this.resort(sheet)`, unlike Usages/Areas below —
      // confirmed live as the actual bug: Features is `forceSort` now
      // (user-requested), so `writeFeatures` just put every row in the
      // correct layer/building/usage/name position; re-applying a stale
      // SAVED manual column-sort right after would silently undo that
      // guarantee the moment a fresh page load restores one.
      this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    }
    const blocks = this.blocks();
    if (Object.keys(blocks).length > 0 || sheetByRole(this.wb, 'blocks')) {
      const sheet = ensureBlocksSheet(this.wb);
      const restored = restoreArrangement(sheet);
      dropDuplicateColumns(this.wb, sheet);
      writeBlocks(sheet, this.blockRows(), true, this.wb);
      if (restored) this.resort(sheet);
      this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    }
    this.migrateAwayMeasuresSheet();
    // Pin the system tabs into one block, in canonical order — whichever of
    // them just got created, and whatever an older, unpinned workbook left.
    normalizeSystemSheetOrder(this.wb);
    // Unlike the three sheets above, this is an RPC rather than a broadcast
    // state key — fetched, not read synchronously. See refreshAnalytics.
    this.refreshAnalytics();
  }

  /**
   * One-time migration for a workbook saved before Features grew its own
   * custom-calc columns: the dedicated Measures sheet is now redundant —
   * everything it showed is a Features-sheet column instead (see
   * `customCalcColumns` above) — so it comes out on the next load.
   *
   * Its OWN cells were never user-authored ("never typed into" —
   * `measuresTable.ts`'s own header comment, now deleted along with the
   * rest of that file) — only its column ORDER was ever meaningful, and
   * that was never the point of keeping the sheet around. So removing it
   * loses nothing of the user's.
   *
   * The one thing that genuinely could break: a formula on ANOTHER sheet
   * that points at `Measures!…`. Deleting the sheet out from under a live
   * reference would turn it into `#REF!` with no warning, which is worse
   * than leaving one stale, never-updated sheet sitting in the tab strip —
   * so a referenced Measures sheet is left in place, inert (it simply never
   * gets written again), rather than deleted.
   */
  private migrateAwayMeasuresSheet(): void {
    const sheet = sheetByRole(this.wb, 'measures');
    if (!sheet) return;
    const referenced = this.wb.sheets.some(
      (s) => s.id !== sheet.id && Object.values(s.cells).some((cell) => cell.f?.includes(`${sheet.name}!`))
    );
    if (referenced) return;
    this.wb.sheets = this.wb.sheets.filter((s) => s.id !== sheet.id);
    if (this.wb.activeSheetId === sheet.id) {
      const fallback = sheetByRole(this.wb, 'areas') ?? sheetByRole(this.wb, 'features');
      this.wb.activeSheetId = (fallback ?? this.wb.sheets[0])?.id ?? this.wb.activeSheetId;
    }
  }

  private analyticsFetching = false;
  private analyticsFetchPending = false;

  /**
   * Fetch `getAnalyticsResult()` and rewrite the Analytics sheet when it
   * resolves. Coalesced like the auto-push paths: a call arriving while one is
   * already in flight runs one more pass afterwards rather than queuing a
   * second overlapping fetch.
   */
  private refreshAnalytics(): void {
    if (this.analyticsFetching) {
      this.analyticsFetchPending = true;
      return;
    }
    this.analyticsFetching = true;
    void getAnalyticsResult()
      .then((result) => {
        if (!hasAnalyticsMeasures(result) && !sheetByRole(this.wb, 'analytics')) return;
        this.emitAsData(() => {
          const sheet = ensureAnalyticsSheet(this.wb);
          const knownFields = new Set(areaColumns(this.data(), this.hiddenFeatures));
          writeAnalyticsSheet(sheet, result, readAnalyticsLayout(), knownFields);
          this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
        });
      })
      .finally(() => {
        this.analyticsFetching = false;
        if (this.analyticsFetchPending) {
          this.analyticsFetchPending = false;
          this.refreshAnalytics();
        }
      });
  }

  /**
   * Fired by the engine when the set of distinct `GI.*EACH` expression
   * arguments changes — declaring (or removing) a custom measure.
   *
   * Deferred to a microtask: the engine calls this MID-RECALCULATION (see
   * engine.ts), and rewriting the Features sheet calls back into the engine
   * (`onRangeEdited`), which must not run while the triggering recalculation
   * is still on the call stack.
   *
   * No dedicated Measures sheet any more (see `migrateAwayMeasuresSheet`) —
   * the custom-calc columns this declares live on Areas itself, via
   * `customCalcColumns`.
   *
   * Does NOT also sync these into Giraffe's own "Spreadsheet / Custom
   * Calculations" analytics category, on purpose — that auto-push used to
   * live here (`syncCustomCalculations`, `analyticsMeasures.ts`, kept as-is,
   * not deleted — it is a real "add a new analytic row" primitive, meant to
   * be driven by something more deliberate later, not every keystroke on
   * this sheet).
   */
  private onMeasureExpressionsChanged(expressions: string[]): void {
    queueMicrotask(() => {
      const areasSheet = sheetByRole(this.wb, 'areas');
      if (areasSheet) {
        this.emitAsData(() => {
          writeAreas(areasSheet, this.data(), this.hiddenFeatures, this.customCalcColumns(expressions), this.wb);
          this.engine.onRangeEdited(areasSheet.id, 1, 1, areasSheet.rows, areasSheet.cols);
        });
      }
    });
  }

  // -- Features: the map's own switch ----------------------------------------

  /**
   * Features switched off on the Features sheet.
   *
   * Held here rather than on the sheet because it is not a property of the
   * feature: it is what THIS app is asking the map to draw, and it is read back
   * out of the Show column on every push so the cells stay the one place the
   * answer lives.
   */
  hiddenFeatures: ReadonlySet<string> = new Set();

  /**
   * Record it on the workbook too, because that is the only copy that survives.
   *
   * The Show column's cells are the runtime answer, and they are on a sheet the
   * project rebuilds — so the ids have to be written somewhere that is actually
   * saved, or a reload brings every hidden feature back.
   */
  private setHiddenFeatures(next: ReadonlySet<string>): void {
    this.hiddenFeatures = next;
    if (next.size > 0) this.wb.hiddenFeatures = [...next].sort();
    else delete this.wb.hiddenFeatures;
  }

  private applyingVisibility = false;
  private visibilityPending = false;

  /**
   * Switching a row off hides it. There is nothing to press.
   *
   * The Show column IS the control — a button beside it would only ask the user
   * to say the same thing twice. Called after any edit on the sheet, and cheap
   * when nothing changed: the map is only told when the set of hidden features
   * actually differs, so typing in a neighbouring column costs no rpc at all.
   * That matters — the bridge stops at 499 invocations a minute.
   */
  /**
   * The Show switch lives on both Features and Areas, bound to the same
   * underlying set — reading both and taking the union means a feature
   * switched off on EITHER sheet is off, consistent with there being no such
   * thing as hiding half of one drawn geometry.
   */
  private hiddenFromSheets(): Set<string> {
    const out = new Set<string>();
    const features = sheetByRole(this.wb, 'features');
    if (features) for (const id of hiddenFromSheet(features, this.data(), this.hiddenFeatures)) out.add(id);
    const areas = sheetByRole(this.wb, 'areas');
    if (areas) for (const id of hiddenFromAreasSheet(areas, this.data(), this.hiddenFeatures)) out.add(id);
    return out;
  }

  autoApplyVisibility(): void {
    if (!sheetByRole(this.wb, 'features') && !sheetByRole(this.wb, 'areas')) return;
    const next = this.hiddenFromSheets();
    if (sameIds(next, this.hiddenFeatures)) return;
    this.setHiddenFeatures(next);
    this.emit();
    void this.applyFeatureVisibility();
  }

  /**
   * Tell the map what to draw.
   *
   * `setSdkRender` is a complete override set, so the hide list goes on every
   * call and the slot is handed back entirely when nothing is hidden — an app
   * holding the override with nothing to say is an app that has quietly taken
   * the map off its owner.
   *
   * Coalesced: a second change while a call is in flight runs one more pass
   * afterwards, which is enough, because a pass sends the whole set rather than
   * a difference.
   */
  async applyFeatureVisibility(): Promise<void> {
    if (!sheetByRole(this.wb, 'features') && !sheetByRole(this.wb, 'areas')) return;
    this.setHiddenFeatures(this.hiddenFromSheets());
    const next = this.hiddenFeatures;
    if (!this.data().live) {
      this.setWriteStatus({
        ok: true,
        detail: next.size > 0 ? `${next.size} would be hidden — no project attached.` : 'Nothing hidden.',
      });
      return;
    }
    if (this.applyingVisibility) {
      this.visibilityPending = true;
      return;
    }
    this.applyingVisibility = true;
    const ok = next.size > 0 ? await setHiddenFeatures([...next]) : await releaseRender();
    this.applyingVisibility = false;
    this.setWriteStatus({
      ok,
      detail: ok
        ? next.size > 0
          ? `${next.size} hidden on the map.`
          : 'Everything is showing again.'
        : 'The host would not take the render override.',
    });
    if (this.visibilityPending) {
      this.visibilityPending = false;
      void this.applyFeatureVisibility();
    }
  }

  showAllFeatures(): void {
    const features = sheetByRole(this.wb, 'features');
    const areas = sheetByRole(this.wb, 'areas');
    if (!features && !areas) return;
    this.pushUndo('Show all features');
    if (features) {
      const showCol = featureColumns(this.data(), this.hiddenFeatures).indexOf(SHOW_FIELD);
      if (showCol >= 0) {
        const rows = this.data().rows('rawSections').length;
        for (let r = FIRST_DATA_ROW; r <= FIRST_DATA_ROW + rows; r++) {
          if (getCell(features, r, 1) !== undefined) setCellValue(features, r, 2 + showCol, true);
        }
      }
      this.engine.onRangeEdited(features.id, 1, 1, features.rows, features.cols);
    }
    if (areas) {
      const showCol = areaColumns(this.data(), this.hiddenFeatures).indexOf(SHOW_FIELD);
      if (showCol >= 0) {
        for (let r = FIRST_DATA_ROW; r <= areas.rows; r++) {
          if (getCell(areas, r, 1) !== undefined) setCellValue(areas, r, 2 + showCol, true);
        }
      }
      this.engine.onRangeEdited(areas.id, 1, 1, areas.rows, areas.cols);
    }
    this.autoApplyVisibility();
  }

  /** Row-label -> feature id, for whichever of Features/Areas is active — both name rows the same way. */
  private rowIdsForSheet(sheet: Sheet): Map<string, string> | null {
    if (sheet.role === 'features') return featureIds(this.data());
    if (sheet.role === 'areas') return areaIds(this.data());
    return null;
  }

  /**
   * A selected ROW's real feature id — `entityKeys` (by physical row
   * number) first, falling back to the name-text lookup only for a sheet
   * that has not been written since `EntityRow.key` shipped. Needed because
   * two rows can now legitimately show the identical name (the " (1)"/"
   * (2)" disambiguator was removed) — re-reading the cell's TEXT and
   * looking it up in a name-keyed map can no longer tell them apart, but
   * `entityKeys` is keyed by the physical row itself, so it always can.
   */
  private idForRow(sheet: Sheet, r: number, ids: Map<string, string>): string | undefined {
    const keyed = sheet.entityKeys?.[r];
    if (keyed !== undefined) return keyed;
    const label = getCell(sheet, r, 1)?.v;
    if (label === undefined || label === null) return undefined;
    return ids.get(String(label).trim().toLowerCase());
  }

  /** Select the features the selected rows name, on the map. */
  async selectFeaturesFromRows(): Promise<void> {
    const sheet = this.sheet();
    const ids = this.rowIdsForSheet(sheet);
    if (!ids) return;
    const picked: string[] = [];
    for (let r = this.sel.r1; r <= this.sel.r2; r++) {
      const id = this.idForRow(sheet, r, ids);
      if (id !== undefined) picked.push(id);
    }
    if (picked.length === 0) return;
    await setSelectedFeatures(picked);
    this.setWriteStatus({ ok: true, detail: `Selected ${picked.length} on the map.` });
  }

  /**
   * Put the map over the features the selected rows name.
   *
   * The other half of `selectFeaturesFromRows`: selecting says WHICH, this says
   * where. A schedule of 300 features is a list of names until one of them can
   * be found on the map, and hunting for it by eye is the thing a spreadsheet
   * beside a map should make unnecessary.
   */
  async zoomToFeaturesFromRows(): Promise<void> {
    const sheet = this.sheet();
    const ids = this.rowIdsForSheet(sheet);
    if (!ids) return;
    const picked: string[] = [];
    for (let r = this.sel.r1; r <= this.sel.r2; r++) {
      const id = this.idForRow(sheet, r, ids);
      if (id !== undefined) picked.push(id);
    }
    await this.zoomToIds(picked);
  }

  /** Fit the map to these features; say so, or say there was nothing to fit. */
  private async zoomToIds(ids: readonly string[]): Promise<void> {
    let box: [[number, number], [number, number]] | null = null;
    let found = 0;
    for (const id of ids) {
      const bounds = boundsOf(this.dataProvider.geometryFor?.(id) as Parameters<typeof boundsOf>[0]);
      if (!bounds) continue;
      found += 1;
      box = box === null ? bounds : [
        [Math.min(box[0][0], bounds[0][0]), Math.min(box[0][1], bounds[0][1])],
        [Math.max(box[1][0], bounds[1][0]), Math.max(box[1][1], bounds[1][1])],
      ];
    }
    if (box === null) {
      this.setWriteStatus({ ok: false, detail: 'Nothing on the map for that row.' });
      return;
    }
    const [[minX, minY], [maxX, maxY]] = box;
    // A single point has no extent to fit, and a map asked to fit a degenerate
    // box zooms as far as it will go — which is not "look at this".
    if (maxX - minX < 1e-7 && maxY - minY < 1e-7) {
      await flyTo({ center: [minX, minY], zoom: 18, duration: 600 });
    } else {
      await fitBounds(box);
    }
    this.setWriteStatus({ ok: true, detail: `Zoomed to ${found} on the map.` });
  }

  /** What the Features sheet says that the project does not. */
  featureDiff(): UsageDiff {
    const sheet = sheetByRole(this.wb, 'features');
    if (!sheet) return { changes: [], removed: [], added: [], renamed: [] };
    // A row removed from this sheet is not a feature to delete — deleting
    // geometry is the map's job, and a sheet that offers it by omission is a
    // sheet nobody can safely sort. Additions and renames likewise: a typed
    // name is a note.
    const diff = featureDiff(sheet, this.data(), this.hiddenFeatures);
    return { changes: diff.changes, removed: [], added: [], renamed: [] };
  }

  private featurePushing = false;
  private featurePushPending = false;

  /**
   * An edited property goes to the feature on its own.
   *
   * Same argument as the Blocks sheet: the column says which property, the row
   * says which feature, the cell says the value, and there is no decision left
   * for a button to carry. Coalesced rather than queued — a pass sends whatever
   * the sheet says at the time, so one more pass afterwards is enough however
   * many edits arrived while it was in flight.
   */
  autoPushFeatures(): void {
    if (!this.data().live) return;
    if (this.featureDiff().changes.length === 0) return;
    if (this.featurePushing) {
      this.featurePushPending = true;
      return;
    }
    this.featurePushing = true;
    void this.pushFeatures().finally(() => {
      this.featurePushing = false;
      if (this.featurePushPending) {
        this.featurePushPending = false;
        this.autoPushFeatures();
      }
    });
  }

  /** Push edited feature properties, then re-read. */
  async pushFeatures(): Promise<PushResult> {
    // Display units on the sheet, metric in the project — see pushUsages.
    const diff = toProjectUnits(this.featureDiff(), this.units());
    if (diff.changes.length === 0) return { ok: true, updated: 0, deleted: 0, detail: 'Nothing to push.' };
    const ids = featureIds(this.data());
    const patches: Record<string, Record<string, unknown>> = {};
    for (const change of diff.changes) {
      const id = ids.get(change.name.toLowerCase());
      if (id === undefined) continue;
      // A cleared cell is a property REMOVED, not one set to "".
      //
      // An empty string is a value: the feature keeps the key and every filter
      // and export downstream keeps a column of blanks. `null` is the host's
      // documented signal for "clear this", and it is what the tag manager
      // sends when a feature's last tag goes.
      (patches[id] ??= {})[change.field] = change.to === '' ? null : change.to;
    }
    const res = await writeFeatureProps(patches);
    const result: PushResult = {
      ok: res.ok,
      updated: res.written,
      deleted: 0,
      detail: res.ok ? `Updated ${res.written} features.` : (res.reason ?? 'The write was refused.'),
    };
    this.setWriteStatus({ ok: result.ok, detail: result.detail });
    await this.refetch?.();
    this.refreshFeaturesSheet(!result.ok);
    return result;
  }

  private refreshFeaturesSheet(keepEdits = false): void {
    const sheet = sheetByRole(this.wb, 'features');
    if (!sheet) return;
    writeFeatures(sheet, this.data(), this.hiddenFeatures, keepEdits, this.wb);
    this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    this.emit();
  }

  // -- Blocks: columns you invent, written onto the definition ---------------

  blocks(): Record<string, BlockDef> {
    return this.data().blocks();
  }

  blockRows(): EntityRow[] {
    const blocks = this.blocks();
    return blockRows(blocks, countInstances(this.data().rows('rawSections')));
  }

  /**
   * Push the Blocks sheet onto the definitions.
   *
   * One write per definition, because the read-merge is per definition: each
   * `updateBlock` replaces `properties` wholesale, so the current properties
   * have to go back with it or the block loses everything else it carries.
   */
  async pushBlocks(): Promise<PushResult> {
    const sheet = sheetByRole(this.wb, 'blocks');
    if (!sheet) return { ok: true, updated: 0, deleted: 0, detail: 'No Blocks sheet.' };
    const blocks = this.blocks();
    const { values, managed } = blockValues(sheet, this.blockRows(), blocks);
    let written = 0;
    for (const [id, own] of values) {
      const block = blocks[id];
      if (!block) continue;
      const res = await writeBlockProps(id, block.properties, own, managed);
      if (!res.ok) {
        const detail = `Wrote ${written}, then ${blockLabel(block)} was refused: ${res.reason ?? 'refused'}.`;
        this.setWriteStatus({ ok: false, detail });
        return { ok: false, updated: written, deleted: 0, detail };
      }
      written += 1;
    }
    const detail = `Wrote ${written} block ${written === 1 ? 'definition' : 'definitions'}.`;
    this.setWriteStatus({ ok: true, detail });
    await this.refetch?.();
    this.refreshBlocksSheet();
    return { ok: true, updated: written, deleted: 0, detail };
  }

  private refreshBlocksSheet(keepEdits = false): void {
    const sheet = sheetByRole(this.wb, 'blocks');
    if (!sheet) return;
    writeBlocks(sheet, this.blockRows(), keepEdits, this.wb);
    this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    this.emit();
  }

  /** What the Usages sheet says that the project does not. */
  usageDiff(): UsageDiff {
    const sheet = this.wb.sheets.find((s) => s.role === 'usages');
    if (!sheet) return { changes: [], removed: [], added: [], renamed: [] };
    return fullDiff(sheet, this.data());
  }

  /** Push it. Deletions are confirmed by name — they remove a project usage. */
  async pushUsages(confirmDelete: (names: string[]) => boolean): Promise<PushResult> {
    // Back into metres and m² on the way out: the project is metric whatever
    // the sheet is showing. `usageDiff` stays in display units because the
    // toolbar lists it to the user.
    const result = await pushUsages(toProjectUnits(this.usageDiff(), this.units()), confirmDelete);
    this.setWriteStatus({ ok: result.ok, detail: result.detail });
    // The project has moved on; read it back before showing it again, or the
    // sheet keeps displaying what was there before the write.
    await this.refetch?.();
    // A refused write keeps what the user typed — they still have it to retry,
    // and losing their typing to a failed write with only an error chip to show
    // for it is the worst of both. A write that WORKED redraws clean.
    this.refreshUsagesSheet(!result.ok);
    return result;
  }

  /** Throw away unpushed edits and re-read the project. The user's Discard. */
  reloadUsages(): void {
    this.pushUndo('Discard usage edits');
    this.refreshUsagesSheet();
  }

  /**
   * Redraw the sheet from the project, with NO undo frame.
   *
   * The read-back after a write is not an edit, and giving it a frame put the
   * post-write state on top of the stack — so Ctrl+Z restored what was already
   * on screen and undo looked broken on this sheet. Only the user's own Discard
   * is undoable, because only that one throws work away.
   */
  private refreshUsagesSheet(keepEdits = false): void {
    const sheet = this.wb.sheets.find((s) => s.role === 'usages');
    if (!sheet) return;
    writeUsages(sheet, this.data(), keepEdits, this.wb);
    this.engine.onRangeEdited(sheet.id, 1, 1, sheet.rows, sheet.cols);
    this.emit();
  }

  // -- Undo -----------------------------------------------------------------

  private frame(label: string): UndoFrame {
    return {
      label,
      // Named ranges too: an import adds them and a sheet delete removes them,
      // so an undo that left them would keep names pointing at missing sheets.
      sheets: JSON.stringify({
        sheets: this.wb.sheets,
        blocks: this.wb.blocks,
        charts: this.wb.charts,
        namedRanges: this.wb.namedRanges,
      }),
      selection: { ...this.sel },
      units: this.units(),
    };
  }

  private pushUndo(label: string): void {
    this.undoStack.push(this.frame(label));
    if (this.undoStack.length > UNDO_LIMIT) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  private restore(frame: UndoFrame): void {
    const parsed = JSON.parse(frame.sheets) as Pick<Workbook, 'sheets' | 'blocks' | 'charts' | 'namedRanges'>;
    this.wb.sheets = parsed.sheets;
    this.wb.blocks = parsed.blocks;
    this.wb.charts = parsed.charts;
    if (parsed.namedRanges) this.wb.namedRanges = parsed.namedRanges;
    else delete this.wb.namedRanges;
    // Back on the sheet the frame was taken on — undoing a sheet delete should
    // land on the sheet that came back, not wherever the tabs happen to start.
    if (sheetById(this.wb, frame.selection.sheetId)) this.wb.activeSheetId = frame.selection.sheetId;
    else if (!sheetById(this.wb, this.wb.activeSheetId)) this.wb.activeSheetId = this.wb.sheets[0]!.id;
    this.sel = sheetById(this.wb, frame.selection.sheetId) ? frame.selection : { ...this.sel, sheetId: this.wb.activeSheetId };
    // An undo that reaches back across a change of units brings the old units
    // with it. The document decides which system is in force, not the frame.
    this.reunitSheets(frame.units, this.units());
    this.engine.rebuildAll();
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): void {
    const frame = this.undoStack.pop();
    if (!frame) return;
    this.redoStack.push(this.frame(frame.label));
    this.restore(frame);
    this.emit();
    this.autoPushAfterHistory();
  }

  redo(): void {
    const frame = this.redoStack.pop();
    if (!frame) return;
    this.undoStack.push(this.frame(frame.label));
    this.restore(frame);
    this.emit();
    this.autoPushAfterHistory();
  }

  /**
   * Undoing a usage edit has to reach the project too.
   *
   * A single cell edit here goes straight to Giraffe, so putting the old value
   * back on the sheet alone would leave the two disagreeing and the sheet
   * quietly wrong. Same rule as the edit itself, which now means the undo goes
   * to the project whatever its size — except when it leaves a usage missing,
   * which is a delete and belongs to the button.
   */
  private autoPushAfterHistory(): void {
    if (this.wb.sheets.some((s) => s.role === 'usages')) this.debouncePush('usages', () => this.autoPushUsages());
  }

  // -- Selection ------------------------------------------------------------

  select(row: number, col: number, extend = false): void {
    const sheet = this.sheet();
    const r = clamp(row, 1, sheet.rows);
    const c = clamp(col, 1, sheet.cols);
    // Landing on a merge selects the whole thing, never one slice of it. An
    // extend grows to take in every merge it touches — the one it started on
    // included — so formatting or clearing it never splits a merge.
    const m = mergeAt(sheet, r, c);
    if (extend) {
      const rect = growToMerges(sheet.merges, {
        r1: Math.min(this.sel.row, r),
        r2: Math.max(this.sel.row, r),
        c1: Math.min(this.sel.col, c),
        c2: Math.max(this.sel.col, c),
      });
      this.sel = { ...this.sel, ...rect };
    } else if (m) {
      this.sel = { sheetId: sheet.id, row: m.r1, col: m.c1, r1: m.r1, c1: m.c1, r2: m.r2, c2: m.c2 };
    } else {
      this.sel = { sheetId: sheet.id, row: r, col: c, r1: r, c1: c, r2: r, c2: c };
    }
    this.emitUiOnly();
  }

  selectRange(r1: number, c1: number, r2: number, c2: number): void {
    const sheet = this.sheet();
    this.sel = {
      sheetId: sheet.id,
      row: clamp(r1, 1, sheet.rows),
      col: clamp(c1, 1, sheet.cols),
      r1: clamp(Math.min(r1, r2), 1, sheet.rows),
      r2: clamp(Math.max(r1, r2), 1, sheet.rows),
      c1: clamp(Math.min(c1, c2), 1, sheet.cols),
      c2: clamp(Math.max(c1, c2), 1, sheet.cols),
    };
    this.emitUiOnly();
  }

  /**
   * Where to step FROM when moving (dRow,dCol) away from (row,col) — the far
   * edge of the merge covering it, in the direction of travel, so crossing a
   * merge is one hop instead of landing back inside the same merge (which
   * `select`'s own merge-snap would otherwise just re-expand into).
   */
  private stepPast(row: number, col: number, dRow: number, dCol: number): { row: number; col: number } {
    const m = mergeAt(this.sheet(), row, col);
    if (!m) return { row, col };
    return {
      row: dRow > 0 ? m.r2 : dRow < 0 ? m.r1 : row,
      col: dCol > 0 ? m.c2 : dCol < 0 ? m.c1 : col,
    };
  }

  move(dRow: number, dCol: number, extend = false): void {
    if (extend) {
      // Grow from the anchor: the corner that moves is the one away from it.
      const r0 = this.sel.r1 === this.sel.row ? this.sel.r2 : this.sel.r1;
      const c0 = this.sel.c1 === this.sel.col ? this.sel.c2 : this.sel.c1;
      const { row: r, col: c } = this.stepPast(r0, c0, dRow, dCol);
      this.select(r + dRow, c + dCol, true);
    } else {
      const { row: r, col: c } = this.stepPast(this.sel.row, this.sel.col, dRow, dCol);
      this.select(r + dRow, c + dCol, false);
    }
  }

  /** Ctrl+arrow: jump to the edge of the current data block, as in Excel. */
  jump(dRow: number, dCol: number, extend = false): void {
    const sheet = this.sheet();
    const r0 = extend ? (this.sel.r1 === this.sel.row ? this.sel.r2 : this.sel.r1) : this.sel.row;
    const c0 = extend ? (this.sel.c1 === this.sel.col ? this.sel.c2 : this.sel.c1) : this.sel.col;
    const seed = this.stepPast(r0, c0, dRow, dCol);
    let r = seed.row;
    let c = seed.col;
    const filled = (rr: number, cc: number): boolean => {
      const cell = getCell(sheet, rr, cc);
      return cell !== undefined && (cell.f !== undefined || (cell.v !== undefined && cell.v !== ''));
    };
    const startFilled = filled(r, c);
    for (let step = 0; step < 10000; step++) {
      const nr = r + dRow;
      const nc = c + dCol;
      if (nr < 1 || nc < 1 || nr > sheet.rows || nc > sheet.cols) break;
      if (startFilled && !filled(nr, nc)) break;
      r = nr;
      c = nc;
      if (!startFilled && filled(r, c)) break;
    }
    this.select(r, c, extend);
  }

  // -- Editing --------------------------------------------------------------

  beginEdit(initial?: string): void {
    if (this.isCellLocked(this.sheet(), this.sel.col)) return;
    const text = initial ?? this.activeRawText();
    this.editing = {
      sheetId: this.sel.sheetId,
      row: this.sel.row,
      col: this.sel.col,
      text,
      caret: text.length,
      pointing: null,
    };
    this.emitUiOnly();
  }

  /** The editor reports what was typed and where the caret is. */
  setEditText(text: string, caret: number): void {
    if (!this.editing) return;
    // Typing anything ends a pick: the reference already in the text stays, and
    // what follows is ordinary formula text again.
    this.editing = { ...this.editing, text, caret, pointing: null };
    // Typing is not an edit until Enter: the document changes in commitEdit.
    this.emitUiOnly();
  }

  setEditCaret(caret: number): void {
    if (!this.editing || this.editing.caret === caret) return;
    this.editing = { ...this.editing, caret, pointing: this.editing.pointing };
    this.emitUiOnly();
  }

  /** F4: cycle the $ anchors of the reference under the caret, as in Excel. */
  toggleRefLock(): void {
    if (!this.editing) return;
    const next = toggleReferenceAt(this.editing.text, this.editing.caret);
    if (!next) return;
    this.setEditText(next.text, next.caret);
  }

  /**
   * Can a cell reference go in at the caret?
   *
   * Only right after something a reference can follow — `=`, an operator, an
   * open paren, a comma. Anywhere else an arrow key means "move the caret" and
   * a click means "I am done here", which is what makes pointing feel like
   * Excel rather than a mode you get stuck in.
   */
  canPoint(): boolean {
    const editing = this.editing;
    if (!editing) return false;
    // Already picking: keep going, wherever the caret now sits.
    if (editing.pointing) return true;
    return canPointAt(editing.text, editing.caret);
  }

  /**
   * Point at a cell (or extend the pick to a range), writing its reference into
   * the formula at the caret and replacing whatever the previous pick wrote.
   */
  pointAt(row: number, col: number, extend = false): void {
    const editing = this.editing;
    if (!editing || !editing.text.startsWith('=')) return;

    const sheet = this.sheet();
    const r = clamp(row, 1, sheet.rows);
    const c = clamp(col, 1, sheet.cols);

    const prev = editing.pointing;
    const start = prev?.start ?? editing.caret;
    const next =
      prev && extend
        ? { start, r1: prev.r1, c1: prev.c1, r2: r, c2: c, sheetId: sheet.id }
        : { start, r1: r, c1: c, r2: r, c2: c, sheetId: sheet.id };

    // A pick on a DIFFERENT sheet from the one being edited is qualified —
    // `Sheet2!A1` — and quoted when the name needs it.
    const prefix = sheet.id === editing.sheetId ? undefined : sheet.name;
    const spliced = applyPick(editing.text, editing.caret, next, prefix);
    this.editing = { ...editing, ...spliced, pointing: next };
    this.emitUiOnly();
  }

  /** Arrow-key pointing: start from the edited cell, then move the pick. */
  movePoint(dRow: number, dCol: number, extend: boolean): void {
    const editing = this.editing;
    if (!editing) return;
    const prev = editing.pointing;
    if (!prev) {
      // The first arrow lands beside the cell being edited, as in Excel.
      this.pointAt(editing.row + dRow, editing.col + dCol, false);
      return;
    }
    if (extend) this.pointAt(prev.r2 + dRow, prev.c2 + dCol, true);
    else this.pointAt(prev.r1 + dRow, prev.c1 + dCol, false);
  }

  cancelEdit(): void {
    const open = this.editing;
    this.editing = null;
    // Come back to the cell that was being edited, wherever the pointing went.
    if (open && this.wb.activeSheetId !== open.sheetId && sheetById(this.wb, open.sheetId)) {
      this.wb.activeSheetId = open.sheetId;
      this.sel = {
        sheetId: open.sheetId,
        row: open.row,
        col: open.col,
        r1: open.row,
        c1: open.col,
        r2: open.row,
        c2: open.col,
      };
    }
    this.emitUiOnly();
  }

  commitEdit(raw?: string, move: 'down' | 'right' | 'none' = 'down'): void {
    // Guard against a second commit from the editor's own blur, which fires as
    // React unmounts the input right after Enter has already committed. Without
    // this the blur writes the same text into the cell Enter just moved to.
    const open = this.editing;
    if (!open) return;
    const { sheetId, row, col } = open;
    const text = raw ?? open.text;
    this.editing = null;
    // Come home before writing: the formula belongs to the cell it started in,
    // wherever the pointing wandered to.
    if (this.wb.activeSheetId !== sheetId && sheetById(this.wb, sheetId)) {
      this.wb.activeSheetId = sheetId;
      this.sel = { sheetId, row, col, r1: row, c1: col, r2: row, c2: col };
    }
    this.setCellRaw(row, col, text, 'Edit cell');
    if (move === 'down') {
      const seed = this.stepPast(row, col, 1, 0);
      this.select(seed.row + 1, seed.col);
    } else if (move === 'right') {
      const seed = this.stepPast(row, col, 0, 1);
      this.select(seed.row, seed.col + 1);
    } else this.emit();
  }

  setCellRaw(row: number, col: number, raw: string, label = 'Edit cell'): void {
    const sheet = this.sheet();
    if (this.isCellLocked(sheet, col)) return;
    if (cellRawText(getCell(sheet, row, col)) === raw) return;
    this.pushUndo(label);
    setCellRaw(sheet, row, col, raw);
    this.engine.onCellEdited(sheet.id, row, col);
    this.emit();
    this.afterValueEdit(sheet);
  }

  /**
   * Every path that changes a VALUE ends here.
   *
   * Typing was wired up and the others were not, so a drag-fill down the Blocks
   * sheet wrote the cells and told the project nothing — the sheet said one
   * thing and the definitions another, with no button left to reconcile them
   * now that these apply themselves. A fill, a paste and a clear are the same
   * act as typing, and there is exactly one place that has to know it.
   */
  private afterValueEdit(sheet: Sheet): void {
    if (sheet.role === 'usages') this.debouncePush('usages', () => this.autoPushUsages());
    if (sheet.role === 'blocks') this.debouncePush('blocks', () => this.autoPushBlocks());
    if (sheet.role === 'features') {
      this.autoApplyVisibility();
      this.debouncePush('features', () => this.autoPushFeatures());
    }
    // Areas is read-only data, but its Show switch is still live — typing
    // into it toggles visibility the same way Features' own does.
    if (sheet.role === 'areas') this.autoApplyVisibility();
  }

  private pushTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Wait for the typing to stop before writing to the project.
   *
   * Each push is an rpc followed by a refetch and a rewrite of the sheet, and
   * entering a column of values one cell at a time fired one of each per cell —
   * the sheet redrawing under the next cell being typed. The pass sends what the
   * sheet says when it runs, so one pass after a burst carries all of it.
   */
  private debouncePush(role: string, push: () => void): void {
    const pending = this.pushTimers.get(role);
    if (pending !== undefined) clearTimeout(pending);
    this.pushTimers.set(
      role,
      setTimeout(() => {
        this.pushTimers.delete(role);
        push();
      }, AUTO_PUSH_DEBOUNCE_MS)
    );
  }

  // -- automatic apply of a block edit ---------------------------------------

  private blockPushing = false;
  private blockPushPending = false;

  /**
   * A value typed onto a block goes to the definition on its own.
   *
   * There is no decision in it: the column says what the property is called,
   * the row says which block, and the cell says the value. A button here would
   * be a button that only ever gets pressed.
   *
   * Coalesced rather than queued — a second edit while a write is in flight
   * runs one more pass afterwards, which is enough, because the pass sends
   * whatever the sheet says at the time rather than a recorded change.
   */
  autoPushBlocks(): void {
    if (!this.data().live) return;
    if (this.blockPushing) {
      this.blockPushPending = true;
      return;
    }
    this.blockPushing = true;
    void this.pushBlocks().finally(() => {
      this.blockPushing = false;
      if (this.blockPushPending) {
        this.blockPushPending = false;
        this.autoPushBlocks();
      }
    });
  }

  /**
   * A row or column change on the Blocks sheet is not a cell edit.
   *
   * Deleting a column removes that property from EVERY block in the project,
   * and deleting a row is a claim about a definition rather than a value. Those
   * ask; typing in a cell does not.
   */
  blockStructuralWarning(axis: 'row' | 'col', from: number, to: number): string | null {
    const sheet = this.sheet();
    if (sheet.role !== 'blocks') return null;
    if (axis === 'col') {
      const fields = fieldsInColumnsOf(sheet, blockColumns(this.blockRows()), from, to);
      const headers = fields.length > 0 ? fields : this.headersIn(from, to);
      if (headers.length === 0) return null;
      return `Remove ${headers.join(', ')} from every block in the project?`;
    }
    const names = this.labelsIn(from, to);
    if (names.length === 0) return null;
    return `Take ${names.join(', ')} off this sheet? The block definitions stay in the project.`;
  }

  private headersIn(from: number, to: number): string[] {
    const sheet = this.sheet();
    const out: string[] = [];
    for (let c = Math.min(from, to); c <= Math.max(from, to); c++) {
      const v = getCell(sheet, 1, c)?.v;
      if (v !== undefined && v !== null && String(v).trim() !== '') out.push(String(v).trim());
    }
    return out;
  }

  private labelsIn(from: number, to: number): string[] {
    const sheet = this.sheet();
    const out: string[] = [];
    for (let r = Math.min(from, to); r <= Math.max(from, to); r++) {
      const v = getCell(sheet, r, 1)?.v;
      if (v !== undefined && v !== null && String(v).trim() !== '') out.push(String(v).trim());
    }
    return out;
  }

  // -- automatic push of a single usage edit ---------------------------------

  private autoPushing = false;
  private usagePushPending = false;
  /** The change last attempted, so a refusal is not retried forever. */
  private autoPushTried: string | null = null;

  /**
   * An edit goes to the project on its own, however many cells it touched.
   *
   * Typing a rate and then hunting for a button is a step that carries no
   * decision, and a pasted block is the same act done twenty times — Features
   * and Blocks have always pushed per edit, and Usages holding back was the one
   * inconsistency left. It is also what made an unpushed Usages edit the only
   * state that could be lost on reload, now that the sheet's cells are rebuilt
   * from the project rather than saved.
   *
   * Cost is one rpc regardless of size: `pushUsages` collects every change and
   * addition into a single `writeUsages` patch.
   *
   * A DELETE is the exception, and stays with the button:
   *
   *   - A deleted ROW shows up as `diff.removed`, and pushing anything while one
   *     is pending would be worse than useless — the push is followed by a
   *     refetch, the project still has the usage because `confirmDelete` said
   *     no, and the row the user just deleted reappears underneath them.
   *   - A deleted COLUMN never reaches this diff at all: a field with no column
   *     is simply absent, and removing it from every usage goes through
   *     `pushFieldRemoval`, which its caller confirms because it is not undoable.
   */
  autoPushUsages(): void {
    // Coalesced like Features and Blocks: an edit landing mid-write gets one
    // more pass afterwards, rather than being dropped until the next keystroke.
    if (this.autoPushing) {
      this.usagePushPending = true;
      return;
    }
    // Only against a real project. Offline, every write is refused, and an
    // error chip appearing because someone typed in the demo is noise.
    if (!this.data().live) return;
    const diff = this.usageDiff();
    if (!autoPushable(diff)) {
      this.autoPushTried = null;
      return;
    }
    // Over the WHOLE diff, not one change: the same paste re-attempted after a
    // refusal has to be recognised as the same paste.
    const key = JSON.stringify([
      diff.changes.map((c) => `${c.name}\u0000${c.field}\u0000${String(c.to)}`).sort(),
      diff.added.map((a) => a.name).sort(),
    ]);
    // Only a REFUSED write blocks a retry of the identical change: a refusal
    // leaves the diff exactly as it was, and without this the next repaint
    // would try it again, and again. A push that WORKED must not block the
    // same value being sent later — undo, redo, and it is the same value.
    if (key === this.autoPushTried) return;

    this.autoPushing = true;
    void this.pushUsages(() => false)
      .then((r) => {
        this.autoPushTried = r.ok ? null : key;
      })
      .finally(() => {
        this.autoPushing = false;
        this.emit();
        if (this.usagePushPending) {
          this.usagePushPending = false;
          this.autoPushUsages();
        }
      });
  }

  // -- column filters --------------------------------------------------------

  /** Cached per sheet against the engine's version, so a scroll costs nothing. */
  private filterCache = new Map<string, { stamp: string; rows: Set<number> }>();

  /** The text a cell shows, which is what a value list ticks and unticks. */
  cellText(sheet: Sheet, row: number, col: number): string {
    const cell = getCell(sheet, row, col);
    if (!cell) return '';
    return formatValue(this.engine.valueAt(sheet, row, col) ?? null, cell.fmt);
  }

  /** Rows the sheet's filters hide — column filters, plus the sheet filter on Areas/Features. Empty when nothing is filtered. */
  filteredRows(sheet: Sheet = this.sheet()): ReadonlySet<number> {
    const columnFiltered = anyFilter(sheet);
    const sheetFiltered = (sheet.role === 'areas' || sheet.role === 'features') && sheetFilterActive(sheet);
    if (!columnFiltered && !sheetFiltered) return NO_ROWS;
    const stamp = `${this.snapshotVersion}:${JSON.stringify(sheet.filters)}:${JSON.stringify(sheet.sheetFilter)}`;
    const hit = this.filterCache.get(sheet.id);
    if (hit && hit.stamp === stamp) return hit.rows;
    const rows = new Set(
      filteredRows(sheet, (r, c) => ({
        value: this.engine.valueAt(sheet, r, c) ?? null,
        text: this.cellText(sheet, r, c),
      }))
    );
    if (sheetFiltered) {
      const colByHeader = new Map<string, number>();
      for (let c = 1; c <= sheet.cols; c++) {
        const h = getCell(sheet, 1, c)?.v;
        if (typeof h === 'string' && h !== '') colByHeader.set(h.toLowerCase(), c);
      }
      // Areas writes a field's own name as its header verbatim; Features
      // prettifies it ("layerId" -> "Layer Id") — the same mismatch
      // `entityTable.ts`'s own header-to-field matching already solves, so
      // every alias a header for this field might wear is tried, not just
      // the literal field name.
      const hidden = sheetFilterHiddenRows(
        sheet,
        (field) => {
          for (const alias of headerAliases(field)) {
            const col = colByHeader.get(alias);
            if (col !== undefined) return col;
          }
          return undefined;
        },
        (r, c) => this.cellText(sheet, r, c),
        FIRST_FILTERABLE_ROW,
        sheet.rows,
        layerVisibility(this.data().visibleDrawingLayers?.bind(this.data()))
      );
      for (const r of hidden) rows.add(r);
    }
    this.filterCache.set(sheet.id, { stamp, rows });
    return rows;
  }

  /**
   * Every distinct value in a column, with how many rows carry it.
   *
   * Read from the DISPLAYED text, because that is what the list shows and what
   * the filter stores — ticking "$8,700" has to mean the same string both times.
   */
  columnValues(col: number): { text: string; count: number }[] {
    const sheet = this.sheet();
    const counts = new Map<string, number>();
    const last = lastContentRow(sheet);
    for (let r = FIRST_FILTERABLE_ROW; r <= last; r++) {
      if (sheet.hiddenRows?.[r]) continue;
      const text = this.cellText(sheet, r, col);
      counts.set(text, (counts.get(text) ?? 0) + 1);
    }
    return [...counts]
      .map(([text, count]) => ({ text, count }))
      .sort((a, b) => naturalCompare(a.text, b.text));
  }

  columnFilter(col: number): ColumnFilter {
    return this.sheet().filters?.[col] ?? {};
  }

  setColumnFilter(col: number, filter: ColumnFilter): void {
    const sheet = this.sheet();
    this.pushUndo('Filter');
    const next = { ...(sheet.filters ?? {}) };
    const empty = (filter.hidden === undefined || filter.hidden.length === 0) && !filter.expr?.trim();
    if (empty) delete next[col];
    else next[col] = filter;
    sheet.filters = Object.keys(next).length > 0 ? next : undefined;
    this.emit();
  }

  // -- column dropdowns ------------------------------------------------------

  columnList(col: number): ColumnList | undefined {
    return listAt(this.sheet(), col);
  }

  rowList(row: number): ColumnList | undefined {
    return rowListAt(this.sheet(), row);
  }

  bandList(axis: 'row' | 'col', index: number): ColumnList | undefined {
    return axis === 'row' ? this.rowList(index) : this.columnList(index);
  }

  setColumnList(col: number, options: string[], colours?: string[]): void {
    this.setBandList('col', col, options.length > 0 ? { options, ...(colours ? { colours } : {}) } : undefined);
  }

  /**
   * One writer for both axes. `undefined` removes the list; a list bound to a
   * field keeps whatever options it was last resolved to, as the offline
   * fallback described on `ColumnList.field`.
   */
  setBandList(axis: 'row' | 'col', index: number, list: ColumnList | undefined): void {
    const sheet = this.sheet();
    this.pushUndo(list ? 'Set dropdown' : 'Remove dropdown');
    const key = axis === 'row' ? 'rowDropdowns' : 'dropdowns';
    const next = { ...(sheet[key] ?? {}) };
    if (!list) delete next[index];
    else next[index] = list;
    sheet[key] = Object.keys(next).length > 0 ? next : undefined;
    this.emit();
  }

  /**
   * Point a band at a project field: every cell in it then offers that field's
   * distinct values, and the list follows the project rather than a copy of it
   * made once.
   */
  bindBandToField(axis: 'row' | 'col', index: number, field: FieldBinding): void {
    // Resolved on the way in so the options are right immediately, and so the
    // saved fallback is written even if the project goes away next.
    this.setBandList(axis, index, resolveList({ options: [], field }, this.fieldValues(field)));
  }

  /**
   * The list a cell obeys, with a bound field's options filled in.
   *
   * Called once per visible cell, so the values behind a binding are cached
   * against the data version — `distinct` over every section, per cell, is the
   * kind of thing that turns scrolling into a slideshow.
   */
  listForCell(row: number, col: number): ColumnList | undefined {
    const list = listForCell(this.sheet(), row, col);
    if (!list?.field) return list;
    return resolveList(list, this.fieldValues(list.field));
  }

  /** Distinct values of a project field, as text. Cached per data version. */
  fieldValues(field: FieldBinding): string[] {
    const data = this.data();
    const key = `${data.version}\u0000${field.source}\u0000${field.name}`;
    const hit = this.fieldValueCache.get(key);
    if (hit) return hit;
    const values = distinct(data.rows(field.source), field.name).map(String);
    // One version's worth. The map is cleared rather than grown because a stale
    // entry here is a dropdown offering values the project no longer has.
    if (this.fieldValueCacheAt !== data.version) {
      this.fieldValueCache.clear();
      this.fieldValueCacheAt = data.version;
    }
    this.fieldValueCache.set(key, values);
    return values;
  }

  private fieldValueCache = new Map<string, string[]>();
  private fieldValueCacheAt = -1;

  /** The distinct values already in a column — the first draft of its list. */
  suggestList(col: number): string[] {
    const sheet = this.sheet();
    const values: string[] = [];
    for (let r = FIRST_FILTERABLE_ROW; r <= lastContentRow(sheet); r++) {
      const text = this.cellText(sheet, r, col).trim();
      if (text !== '') values.push(text);
    }
    return suggestOptions(values);
  }

  /** Pick a value from a chip. Writes it as an ordinary edit, undo and all. */
  chooseOption(row: number, col: number, option: string): void {
    this.setCellRaw(row, col, option, 'Choose');
  }

  clearFilters(): void {
    const sheet = this.sheet();
    if (!sheet.filters) return;
    this.pushUndo('Clear filters');
    sheet.filters = undefined;
    this.emit();
  }

  clearSelection(): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    this.pushUndo('Clear');
    if (sheet.role === 'areas') {
      // Column by column, not the one bulk call: a rectangular clear cannot
      // skip the one locked column sitting in the middle of a wider
      // selection (every column but Show).
      for (let c = c1; c <= c2; c++) {
        if (!this.isCellLocked(sheet, c)) clearCells(sheet, r1, c, r2, c);
      }
    } else {
      clearCells(sheet, r1, c1, r2, c2);
    }
    /*
     * Clearing a database's row clears the database. Otherwise its formulas go
     * and its sub-rows stay, hanging under an empty row with nothing to say
     * what they are — and Delete is what anyone presses to be rid of it.
     */
    for (const db of Object.values(sheet.databases ?? {})) {
      const { row, col } = db.anchor;
      if (row < r1 || row > r2 || col + db.width - 1 < c1 || col > c2) continue;
      removeDatabase(sheet, this.engine, db);
    }
    this.engine.onRangeEdited(sheet.id, r1, c1, r2, c2);
    this.emit();
    this.afterValueEdit(sheet);
  }

  // -- Merges -----------------------------------------------------------------

  /** Would merging this rect throw away a value sitting in anything but its anchor? */
  mergeWouldDiscard(r1: number, c1: number, r2: number, c2: number): boolean {
    const sheet = this.sheet();
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        if (r === r1 && c === c1) continue;
        const cell = getCell(sheet, r, c);
        if (cell && (cell.f !== undefined || (cell.v !== undefined && cell.v !== ''))) return true;
      }
    }
    return false;
  }

  /**
   * Merge a rect, keeping only its anchor's content. Refuses silently — no
   * undo pushed — when the rect partially overlaps a DIFFERENT existing
   * merge; re-merging the exact same rect is a harmless no-op, not a
   * conflict, so that case is let through.
   *
   * `silent` skips the undo push and the `afterValueEdit` hook, because a
   * paste that recreates merges already pushed and hooked its own frame.
   */
  mergeCells(r1: number, c1: number, r2: number, c2: number, opts?: { silent?: boolean }): void {
    const sheet = this.sheet();
    const existing = overlapsAny(sheet.merges, r1, c1, r2, c2);
    const exact = existing && existing.r1 === r1 && existing.c1 === c1 && existing.r2 === r2 && existing.c2 === c2;
    if (existing && !exact) return; // conflicts with a different merge — refuse
    if (exact) return; // already exactly this merge — nothing to do
    if (!opts?.silent) this.pushUndo('Merge cells');
    if (c2 > c1) clearCells(sheet, r1, c1 + 1, r1, c2);
    if (r2 > r1) clearCells(sheet, r1 + 1, c1, r2, c2);
    sheet.merges = [...(sheet.merges ?? []), { r1, c1, r2, c2 }];
    this.engine.onRangeEdited(sheet.id, r1, c1, r2, c2);
    this.selectRange(r1, c1, r2, c2);
    if (!opts?.silent) this.afterValueEdit(sheet);
    else this.emit();
  }

  /** The merge that IS the current selection, exactly — what backs the Unmerge menu item. */
  mergeInSelection(): Merge | undefined {
    const { r1, c1, r2, c2 } = this.sel;
    return this.sheet().merges?.find((m) => m.r1 === r1 && m.c1 === c1 && m.r2 === r2 && m.c2 === c2);
  }

  unmergeCells(merge: Merge): void {
    const sheet = this.sheet();
    this.pushUndo('Unmerge cells');
    const next = (sheet.merges ?? []).filter(
      (m) => !(m.r1 === merge.r1 && m.c1 === merge.c1 && m.r2 === merge.r2 && m.c2 === merge.c2)
    );
    sheet.merges = next.length > 0 ? next : undefined;
    this.selectRange(merge.r1, merge.c1, merge.r2, merge.c2);
    this.emit();
  }

  applyFormat(fmt: FormatId | undefined): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    this.pushUndo('Format');
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) setCellFormat(sheet, r, c, fmt);
    this.emit();
  }

  applyStyle(patch: Partial<CellStyle>): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    this.pushUndo('Style');
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const key = `${r},${c}`;
        const prev = sheet.cells[key] ?? {};
        const style: CellStyle = { ...prev.s, ...patch };
        for (const k of Object.keys(style) as (keyof CellStyle)[]) {
          // `false` means "off" for the boolean flags, so it is dropped rather
          // than stored — but an empty borders object is dropped too, or a
          // cleared border leaves `{borders:{}}` behind on every cell forever.
          if (style[k] === undefined || style[k] === false) delete style[k];
        }
        if (style.borders && Object.keys(style.borders).length === 0) delete style.borders;
        const next = { ...prev, s: Object.keys(style).length > 0 ? style : undefined };
        if (next.s === undefined) delete next.s;
        if (Object.keys(next).length === 0) delete sheet.cells[key];
        else sheet.cells[key] = next;
      }
    }
    this.emit();
  }

  /**
   * Set or clear one border target across the selection.
   *
   * An edge means the edge OF THE SELECTION, not of every cell in it — clicking
   * Top on a three-row range draws one line above the range, not a line above
   * each row. That is Excel's behaviour and the only reading that makes the
   * buttons useful on more than one cell. `inside` is the other half of it: the
   * internal gridlines, without touching the outside.
   *
   * Each target OVERWRITES the edges it names and leaves every other edge of
   * the cell alone, so Top then Left builds up a corner rather than replacing
   * what came before.
   */
  applyBorders(target: BorderTarget, border: Border | null): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    this.pushUndo('Borders');

    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const key = `${r},${c}`;
        const prev = sheet.cells[key] ?? {};
        const borders: Partial<Record<BorderEdge, Border>> = { ...prev.s?.borders };

        for (const edge of edgesFor(target, { r, c, r1, c1, r2, c2 })) {
          if (target === 'none' || border === null) delete borders[edge];
          else borders[edge] = { ...border };
        }

        const style: CellStyle = { ...prev.s };
        if (Object.keys(borders).length > 0) style.borders = borders;
        else delete style.borders;

        const next = { ...prev, s: Object.keys(style).length > 0 ? style : undefined };
        if (next.s === undefined) delete next.s;
        if (Object.keys(next).length === 0) delete sheet.cells[key];
        else sheet.cells[key] = next;
      }
    }
    this.emit();
  }

  /** Strip every visual style from the selection, leaving the values. */
  clearFormatting(): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    this.pushUndo('Clear formatting');
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const key = `${r},${c}`;
        const prev = sheet.cells[key];
        if (!prev) continue;
        const next = { ...prev };
        delete next.s;
        delete next.fmt;
        if (next.f === undefined && next.v === undefined) delete sheet.cells[key];
        else sheet.cells[key] = next;
      }
    }
    /*
     * Dropdowns too. A list belongs to its whole column (or row), not to a cell,
     * so clearing the selection clears the list of every band it touches.
     * Not on the system sheets: their lists are the project's own values — a
     * feature's layer, a usage's colour — and come straight back on the next
     * broadcast anyway.
     */
    if (!sheet.role) {
      if (sheet.dropdowns) for (let c = c1; c <= c2; c++) delete sheet.dropdowns[c];
      if (sheet.rowDropdowns) for (let r = r1; r <= r2; r++) delete sheet.rowDropdowns[r];
      if (sheet.dropdowns && Object.keys(sheet.dropdowns).length === 0) delete sheet.dropdowns;
      if (sheet.rowDropdowns && Object.keys(sheet.rowDropdowns).length === 0) delete sheet.rowDropdowns;
    }
    this.emit();
  }

  /** The number format on the active cell, for the menu's dropdown. */
  activeFormat(): FormatId | undefined {
    return getCell(this.sheet(), this.sel.row, this.sel.col)?.fmt;
  }

  /** The style of the active cell, for showing current colours in the menu. */
  activeStyle(): CellStyle {
    return getCell(this.sheet(), this.sel.row, this.sel.col)?.s ?? {};
  }

  /** True when every cell in the selection already has the style flag set. */
  selectionHas(flag: keyof CellStyle): boolean {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        if (!getCell(sheet, r, c)?.s?.[flag]) return false;
      }
    }
    return true;
  }

  setColWidth(col: number, width: number): void {
    this.sheet().colWidths[col] = Math.max(32, Math.round(width));
    this.emit();
  }

  setRowHeight(row: number, height: number): void {
    this.sheet().rowHeights[row] = Math.max(16, Math.round(height));
    this.emit();
  }

  // -- Clipboard ------------------------------------------------------------

  copySelection(cut = false): ClipboardPayload {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    const cells: string[][] = [];
    const display: string[][] = [];
    const formats: (FormatId | undefined)[][] = [];
    const styles: (CellStyle | undefined)[][] = [];
    for (let r = r1; r <= r2; r++) {
      const raw: string[] = [];
      const shown: string[] = [];
      const fmts: (FormatId | undefined)[] = [];
      const sts: (CellStyle | undefined)[] = [];
      for (let c = c1; c <= c2; c++) {
        const cell = getCell(sheet, r, c);
        raw.push(cellRawText(cell));
        shown.push(formatValue(this.engine.valueAt(sheet, r, c) ?? null, cell?.fmt));
        fmts.push(cell?.fmt);
        sts.push(cell?.s ? structuredClone(cell.s) : undefined);
      }
      cells.push(raw);
      display.push(shown);
      formats.push(fmts);
      styles.push(sts);
    }
    // Only merges fully inside the copied rect come along — a merge the
    // selection only partly covers is dropped, not clipped, matching Excel
    // (which refuses to copy a fractional merge at all).
    const merges = (sheet.merges ?? [])
      .filter((m) => m.r1 >= r1 && m.r2 <= r2 && m.c1 >= c1 && m.c2 <= c2)
      .map((m) => ({ r1: m.r1 - r1, c1: m.c1 - c1, r2: m.r2 - r1, c2: m.c2 - c1 }));
    this.clipboard = {
      cells,
      display,
      formats,
      styles,
      origin: { row: r1, col: c1 },
      cut,
      merges: merges.length > 0 ? merges : undefined,
    };
    return this.clipboard;
  }

  /**
   * The in-app payload, when the text on the system clipboard is still ours.
   *
   * Comparing the text is what distinguishes "the user copied here and pasted
   * here" — where formulas and formats should survive intact — from "the user
   * copied out of Excel", where all we have is text.
   */
  internalPayloadFor(text: string): ClipboardPayload | null {
    const held = this.clipboard;
    if (!held) return null;
    return toTsv(held.display) === text || toTsv(held.cells) === text ? held : null;
  }

  /**
   * Cut, copy and paste driven from the MENU rather than from a keystroke.
   *
   * `document.execCommand('copy')` only works while the grid holds focus, and
   * `execCommand('paste')` is blocked outright by every current browser — so
   * three menu items wired that way would simply do nothing. These write and
   * read the clipboard directly, and report a refusal instead of failing
   * silently.
   */
  async clipboardCopy(cut: boolean): Promise<boolean> {
    const payload = this.copySelection(cut);
    try {
      await navigator.clipboard.writeText(toTsv(payload.display));
    } catch {
      return false;
    }
    if (cut) this.clearSelection();
    return true;
  }

  async clipboardPaste(): Promise<boolean> {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return false;
    }
    if (!text) return true;
    const internal = this.internalPayloadFor(text);
    if (internal) {
      this.pasteCells(internal.cells, internal.origin, internal);
      return true;
    }
    // Same formatting-from-HTML path as the keyboard paste (Grid.tsx's
    // onPaste) — `readText()` alone only ever gives back the plain-text TSV.
    const html = await this.clipboardReadHtml();
    const parsed = html ? parseHtmlTable(html) : null;
    if (parsed) this.pasteCells(parsed.values, null, { formats: parsed.formats, styles: parsed.styles, merges: parsed.merges });
    else this.pasteCells(parseTsv(text), null);
    return true;
  }

  /** `navigator.clipboard.read()` is not universally supported; a failure here just means no formatting. */
  private async clipboardReadHtml(): Promise<string | null> {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes('text/html')) return await (await item.getType('text/html')).text();
      }
    } catch {
      // Fall through to the TSV-only path.
    }
    return null;
  }

  /**
   * Paste raw cell text at the selection. Formulas are translated by the offset
   * from where they were copied — but only when they came from this app, since
   * text pasted in from Excel has no origin to translate against.
   */
  pasteCells(
    cells: string[][],
    origin: { row: number; col: number } | null,
    meta?: Pick<ClipboardPayload, 'formats' | 'styles' | 'merges'>
  ): void {
    if (cells.length === 0) return;
    const sheet = this.sheet();
    const { row, col } = this.sel;

    // One cell pasted into a range fills the range, as in Excel — otherwise
    // "copy this rate down the column" needs a paste per row.
    const single = cells.length === 1 && cells[0]!.length === 1;
    const spread = this.sel.r2 > this.sel.r1 || this.sel.c2 > this.sel.c1;
    if (single && spread) {
      const value = cells[0]![0]!;
      cells = [];
      for (let r = this.sel.r1; r <= this.sel.r2; r++) {
        cells.push(new Array(this.sel.c2 - this.sel.c1 + 1).fill(value));
      }
    }
    const dRow = origin ? row - origin.row : 0;
    const dCol = origin ? col - origin.col : 0;
    this.pushUndo('Paste');
    let maxR = row;
    let maxC = col;
    for (let i = 0; i < cells.length; i++) {
      const line = cells[i]!;
      for (let j = 0; j < line.length; j++) {
        const target = { r: row + i, c: col + j };
        if (this.isCellLocked(sheet, target.c)) continue;
        if (isCoveredNonAnchor(sheet, target.r, target.c)) continue;
        let raw = line[j]!;
        if (raw.startsWith('=') && (dRow !== 0 || dCol !== 0)) {
          raw = `=${translateFormula(raw.slice(1), dRow, dCol)}`;
        }
        setCellRaw(sheet, target.r, target.c, raw);
        // An internal paste carries the look as well as the value — copying a
        // formatted cell and losing its format is not what "copy" means.
        if (meta) {
          const key = `${target.r},${target.c}`;
          const fmt = meta.formats[i]?.[j];
          const style = meta.styles[i]?.[j];
          const cell = sheet.cells[key];
          if (cell && (fmt !== undefined || style !== undefined)) {
            sheet.cells[key] = { ...cell, fmt, s: style ? structuredClone(style) : undefined };
            if (sheet.cells[key]!.fmt === undefined) delete sheet.cells[key]!.fmt;
            if (sheet.cells[key]!.s === undefined) delete sheet.cells[key]!.s;
          }
        }
        maxR = Math.max(maxR, target.r);
        maxC = Math.max(maxC, target.c);
      }
    }
    // Re-offset any merges that rode along and recreate them at the paste
    // target — silently, since a conflicting merge is just skipped rather
    // than failing the whole paste, and the undo frame is already pushed.
    for (const m of meta?.merges ?? []) {
      this.mergeCells(row + m.r1, col + m.c1, row + m.r2, col + m.c2, { silent: true });
    }
    this.engine.onRangeEdited(sheet.id, row, col, maxR, maxC);
    this.selectRange(row, col, maxR, maxC);
    this.afterValueEdit(sheet);
  }

  /**
   * Drag-fill.
   *
   * Excel's behaviour, not a tile: a source that reads as a SERIES continues it
   * (1,2,3 -> 4,5,6; 2,4,6 -> 8,10,12; Jan, Feb, Mar -> Apr; Unit 01 -> Unit 02),
   * and only a source with no readable series repeats. Formulas always translate
   * their relative references rather than counting.
   *
   * `copyOnly` (the modifier key) forces the repeat, which is the escape hatch
   * for "I really do want 1,2,3,1,2,3".
   */
  fillTo(target: { row: number; col: number }, copyOnly = false): void {
    const sheet = this.sheet();
    const src = { r1: this.sel.r1, c1: this.sel.c1, r2: this.sel.r2, c2: this.sel.c2 };
    const r1 = Math.min(src.r1, target.row);
    const r2 = Math.max(src.r2, target.row);
    const c1 = Math.min(src.c1, target.col);
    const c2 = Math.max(src.c2, target.col);
    if (r1 === src.r1 && r2 === src.r2 && c1 === src.c1 && c2 === src.c2) return;

    const vertical = r1 < src.r1 || r2 > src.r2;
    const reverse = vertical ? r1 < src.r1 : c1 < src.c1;
    const srcRows = src.r2 - src.r1 + 1;
    const srcCols = src.c2 - src.c1 + 1;

    this.pushUndo('Fill');

    // One series per line across the fill axis: filling a two-column block down
    // reads a separate series for each column, which is what makes a date
    // column beside an amount column behave.
    const lines = vertical ? srcCols : srcRows;
    const series: (Series | null)[] = [];
    for (let i = 0; i < lines; i++) {
      if (copyOnly) {
        series.push(null);
        continue;
      }
      const cells: SeriesCell[] = [];
      let literal = true;
      for (let j = 0; j < (vertical ? srcRows : srcCols); j++) {
        const r = vertical ? src.r1 + j : src.r1 + i;
        const c = vertical ? src.c1 + i : src.c1 + j;
        const cell = getCell(sheet, r, c);
        // A formula continues by reference translation, never by counting.
        if (cell?.f !== undefined) {
          literal = false;
          break;
        }
        cells.push({ value: cell?.v ?? null, fmt: cell?.fmt });
      }
      series.push(literal ? detectSeries(cells, reverse) : null);
    }

    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        if (r >= src.r1 && r <= src.r2 && c >= src.c1 && c <= src.c2) continue;
        if (this.isCellLocked(sheet, c)) continue;
        if (isCoveredNonAnchor(sheet, r, c)) continue;

        const lineIndex = vertical ? c - src.c1 : r - src.r1;
        const step = vertical
          ? reverse
            ? src.r1 - r
            : r - src.r2
          : reverse
            ? src.c1 - c
            : c - src.c2;
        const line = series[lineIndex];

        if (line && step > 0) {
          const produced = line.at(step);
          setCellValue(sheet, r, c, produced.value, produced.fmt);
          const source = getCell(sheet, vertical ? src.r1 : r, vertical ? c : src.c1);
          if (source?.s) sheet.cells[`${r},${c}`] = { ...sheet.cells[`${r},${c}`], s: source.s };
          continue;
        }

        // Tile the source block — the fallback, and the copyOnly path.
        const sr = src.r1 + mod(r - src.r1, srcRows);
        const sc = src.c1 + mod(c - src.c1, srcCols);
        const raw = cellRawText(getCell(sheet, sr, sc));
        const next = raw.startsWith('=') ? `=${translateFormula(raw.slice(1), r - sr, c - sc)}` : raw;
        setCellRaw(sheet, r, c, next);
        const style = getCell(sheet, sr, sc);
        if (style?.fmt || style?.s) {
          sheet.cells[`${r},${c}`] = { ...sheet.cells[`${r},${c}`], fmt: style.fmt, s: style.s };
        }
      }
    }

    this.engine.onRangeEdited(sheet.id, r1, c1, r2, c2);
    this.selectRange(r1, c1, r2, c2);
    this.afterValueEdit(sheet);
  }

  /** Fill the selection down from its first row (Ctrl+D). */
  fillDown(): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    if (r2 <= r1) return;
    this.pushUndo('Fill down');
    for (let c = c1; c <= c2; c++) {
      if (this.isCellLocked(sheet, c)) continue;
      const src = cellRawText(getCell(sheet, r1, c));
      for (let r = r1 + 1; r <= r2; r++) {
        if (isCoveredNonAnchor(sheet, r, c)) continue;
        const raw = src.startsWith('=') ? `=${translateFormula(src.slice(1), r - r1, 0)}` : src;
        setCellRaw(sheet, r, c, raw);
      }
    }
    this.engine.onRangeEdited(sheet.id, r1, c1, r2, c2);
    this.emit();
    this.afterValueEdit(sheet);
  }

  /** Fill the selection right from its first column (Ctrl+R). */
  fillRight(): void {
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    if (c2 <= c1) return;
    this.pushUndo('Fill right');
    for (let r = r1; r <= r2; r++) {
      const src = cellRawText(getCell(sheet, r, c1));
      for (let c = c1 + 1; c <= c2; c++) {
        if (this.isCellLocked(sheet, c)) continue;
        if (isCoveredNonAnchor(sheet, r, c)) continue;
        const raw = src.startsWith('=') ? `=${translateFormula(src.slice(1), 0, c - c1)}` : src;
        setCellRaw(sheet, r, c, raw);
      }
    }
    this.engine.onRangeEdited(sheet.id, r1, c1, r2, c2);
    this.emit();
    this.afterValueEdit(sheet);
  }

  // -- Find -----------------------------------------------------------------

  /**
   * Search results. Recomputed on demand rather than kept in sync — a sheet
   * changes far more often than anyone presses Ctrl+F, and a stale match list
   * that jumps to the wrong cell is worse than a moment's work.
   */
  search: { query: string; allSheets: boolean; matches: FindMatch[]; index: number } | null = null;

  /**
   * Find. Matches the DISPLAYED text and the formula source both: someone
   * looking for `GI.TOTAL` means the formula, and someone looking for
   * `Residential` means what they can see, and neither should have to say which.
   */
  find(query: string, allSheets = false): void {
    const trimmed = query.trim();
    if (trimmed === '') {
      this.search = null;
      this.emit();
      return;
    }
    const needle = trimmed.toLowerCase();
    const matches: FindMatch[] = [];
    const sheets = allSheets ? this.wb.sheets : [this.sheet()];
    for (const sheet of sheets) {
      for (const key of Object.keys(sheet.cells)) {
        const { row, col } = parseCk(key);
        const cell = sheet.cells[key]!;
        const shown = formatValue(this.engine.valueAt(sheet, row, col) ?? null, cell.fmt);
        const formula = cell.f !== undefined ? `=${cell.f}` : '';
        if (!shown.toLowerCase().includes(needle) && !formula.toLowerCase().includes(needle)) continue;
        matches.push({ sheetId: sheet.id, row, col, text: shown || formula });
      }
    }
    // Reading order, so Next walks the sheet the way the eye does.
    matches.sort((a, b) => a.sheetId.localeCompare(b.sheetId) || a.row - b.row || a.col - b.col);

    // Resume from where the cursor is rather than from the top: pressing Next
    // should go to the match after the one you are looking at.
    const from = matches.findIndex(
      (m) => m.sheetId === this.sel.sheetId && (m.row > this.sel.row || (m.row === this.sel.row && m.col >= this.sel.col))
    );
    this.search = { query: trimmed, allSheets, matches, index: matches.length === 0 ? -1 : Math.max(from, 0) };
    this.emit();
    if (matches.length > 0) this.goToMatch(0);
  }

  /** Step through the matches, wrapping at both ends. */
  goToMatch(delta: number): void {
    const search = this.search;
    if (!search || search.matches.length === 0) return;
    const n = search.matches.length;
    const index = ((search.index + delta) % n + n) % n;
    const match = search.matches[index]!;
    this.search = { ...search, index };
    if (match.sheetId !== this.wb.activeSheetId) this.wb.activeSheetId = match.sheetId;
    this.sel = {
      sheetId: match.sheetId,
      row: match.row,
      col: match.col,
      r1: match.row,
      c1: match.col,
      r2: match.row,
      c2: match.col,
    };
    this.emit();
  }

  closeSearch(): void {
    if (!this.search) return;
    this.search = null;
    this.emit();
  }

  /** True when a cell is one of the current matches, for the highlight. */
  isMatch(sheetId: string, row: number, col: number): boolean {
    const search = this.search;
    if (!search) return false;
    return search.matches.some((m) => m.sheetId === sheetId && m.row === row && m.col === col);
  }

  // -- Rows and columns -----------------------------------------------------

  /** Select whole rows, as clicking or dragging a row header does. */
  selectRows(from: number, to: number): void {
    const sheet = this.sheet();
    const r1 = clamp(Math.min(from, to), 1, sheet.rows);
    const r2 = clamp(Math.max(from, to), 1, sheet.rows);
    this.sel = { sheetId: sheet.id, row: r1, col: 1, r1, c1: 1, r2, c2: sheet.cols, band: 'row' };
    this.emit();
  }

  selectCols(from: number, to: number): void {
    const sheet = this.sheet();
    const c1 = clamp(Math.min(from, to), 1, sheet.cols);
    const c2 = clamp(Math.max(from, to), 1, sheet.cols);
    this.sel = { sheetId: sheet.id, row: 1, col: c1, r1: 1, c1, r2: sheet.rows, c2, band: 'col' };
    this.emit();
  }

  /** The whole rows or columns currently selected, if the selection is a band. */
  selectedBand(): { axis: 'row' | 'col'; from: number; to: number; count: number } | null {
    const { band, r1, r2, c1, c2 } = this.sel;
    if (band === 'row') return { axis: 'row', from: r1, to: r2, count: r2 - r1 + 1 };
    if (band === 'col') return { axis: 'col', from: c1, to: c2, count: c2 - c1 + 1 };
    return null;
  }

  insertBand(axis: 'row' | 'col', at: number, count: number): void {
    const sheet = this.sheet();
    this.pushUndo(axis === 'row' ? 'Insert rows' : 'Insert columns');
    insertBand(this.wb, sheet, axis, at, count);
    // A structural change rewrites formulas across every sheet, so the whole
    // dependency graph has to be rebuilt rather than patched.
    this.engine.rebuildAll();
    if (axis === 'row') this.selectRows(at, at + count - 1);
    else this.selectCols(at, at + count - 1);
  }

  /**
   * Deleting rows on the Usages sheet deletes USAGES.
   *
   * Everywhere else a deleted row is a sheet edit. Here the sheet is a view of
   * the project, so removing a row means removing the usage — and that is a
   * direct, confirmed act rather than something staged: it is asked for by
   * name, done at once, and the sheet is re-read afterwards.
   *
   * Returns the names it needs confirming, or null when this is an ordinary
   * row delete.
   */
  usagesToDelete(axis: 'row' | 'col', from: number, to: number): string[] | null {
    const sheet = this.sheet();
    if (sheet.role !== 'usages' || axis !== 'row') return null;
    const names: string[] = [];
    for (let r = Math.min(from, to); r <= Math.max(from, to); r++) {
      const label = getCell(sheet, r, 1)?.v;
      if (label === undefined || label === null) continue;
      const name = String(label).trim();
      if (name !== '') names.push(name);
    }
    return names;
  }

  /**
   * What the last push or delete said. Shown in the toolbar, because a write
   * that reaches the project can fail and a failure the user cannot see is
   * indistinguishable from one that worked.
   */
  writeStatus: { ok: boolean; detail: string } | null = null;

  private writeStatusTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Say it, then stop saying it.
   *
   * These are notes about something that already happened — "Selected 2 on the
   * map", "updated 3" — and a note that never leaves becomes furniture: it is
   * still on screen ten minutes later describing an action nobody remembers
   * taking, which is worse than not having said it. Ten seconds is long enough
   * to read one line and notice a warning.
   *
   * Emitted as ambient, not as an edit: a chip fading is not the user typing,
   * and billing it to them would put "Unsaved changes" on the document ten
   * seconds after a map selection.
   */
  setWriteStatus(status: { ok: boolean; detail: string } | null): void {
    if (this.writeStatusTimer) clearTimeout(this.writeStatusTimer);
    this.writeStatusTimer = null;
    this.writeStatus = status;
    if (status) {
      this.writeStatusTimer = setTimeout(() => {
        this.writeStatusTimer = null;
        this.writeStatus = null;
        this.emitAsData();
      }, WRITE_STATUS_MS);
    }
    this.emitAsData();
  }

  /**
   * Deleting a column on the Usages sheet deletes the FIELD from every usage.
   *
   * The same argument as a row: the sheet is a view of the project, so the
   * column is not a column, it is `salePrice` on all of them. Returns the field
   * names needing confirmation, or null when this is an ordinary column delete.
   */
  usageFieldsToDelete(axis: 'row' | 'col', from: number, to: number): string[] | null {
    const sheet = this.sheet();
    if (sheet.role !== 'usages' || axis !== 'col') return null;
    const fields = fieldsInColumns(sheet, this.data(), from, to);
    return fields.length > 0 ? fields : null;
  }

  /** Remove fields from every usage, then re-read the project and the sheet. */
  async deleteUsageFields(fields: readonly string[]): Promise<PushResult> {
    this.setWriteStatus({ ok: true, detail: `Removing ${fields.join(', ')}…` });
    const result = await pushFieldRemoval(fields);
    this.setWriteStatus({ ok: result.ok, detail: result.detail });
    await this.refetch?.();
    this.refreshUsagesSheet();
    return result;
  }

  /** Delete usages from the project, then re-read the project and the sheet. */
  async deleteUsages(names: readonly string[]): Promise<PushResult> {
    this.setWriteStatus({ ok: true, detail: `Deleting ${names.join(', ')}…` });
    const result = await pushUsages({ changes: [], removed: [...names], added: [], renamed: [] }, () => true);
    this.setWriteStatus({ ok: result.ok, detail: result.detail });
    // Re-fetch BEFORE rewriting the sheet, either way: on success the usage is
    // gone, and on failure the sheet should show what the project actually has.
    await this.refetch?.();
    this.refreshUsagesSheet();
    return result;
  }

  deleteBand(axis: 'row' | 'col', from: number, to: number): void {
    const sheet = this.sheet();
    const indices: number[] = [];
    for (let i = Math.min(from, to); i <= Math.max(from, to); i++) indices.push(i);
    if (indices.length === 0) return;
    this.pushUndo(axis === 'row' ? 'Delete rows' : 'Delete columns');
    deleteBand(this.wb, sheet, axis, indices);
    this.engine.rebuildAll();
    if (axis === 'row') this.selectRows(indices[0]!, indices[0]!);
    else this.selectCols(indices[0]!, indices[0]!);
  }

  /**
   * Sort the rows by a column.
   *
   * Through `applyStructural`, like every other row move, so the FORMULAS
   * follow — a sort that reorders cells and leaves `=B7` pointing at row 7
   * silently changes what every total means, and it is easy to miss because the
   * numbers still look plausible.
   *
   * Row 1 is the header and stays put, the same rule the filters use. The range
   * ends at the last row with anything in it, so a sort does not drag two
   * hundred blanks through the permutation.
   */
  sortByColumn(col: number, descending = false): void {
    const sheet = this.sheet();
    // Checked before the undo entry: a sheet with nothing to sort must not leave
    // an undo step that does nothing.
    if (lastContentRow(sheet) <= FIRST_FILTERABLE_ROW) return;
    this.pushUndo(descending ? 'Sort Z→A' : 'Sort A→Z');
    this.sortRows(sheet, col, descending);
    /*
     * Recorded, not just performed.
     *
     * On a user sheet this is only a note — the sorted cells are what gets
     * saved. On a SYSTEM sheet the cells are rebuilt from the project in the
     * project's own order on every load, so this record IS the sort: without it
     * a sorted table comes back shuffled.
     */
    sheet.sort = { col, descending };
    this.engine.rebuildAll();
    this.emit();
    this.afterValueEdit(sheet);
  }

  /** The permutation itself, for both the user's sort and a restored one. */
  private sortRows(sheet: Sheet, col: number, descending: boolean): boolean {
    const from = FIRST_FILTERABLE_ROW;
    const to = lastContentRow(sheet);
    if (to <= from) return false;
    // By the VALUE, so a formula column sorts by its result rather than by the
    // text of the formula.
    applyStructural(
      this.wb,
      sheet,
      'row',
      sortMap(from, to, (r) => this.engine.valueAt(sheet, r, col) ?? getCell(sheet, r, col)?.v, descending)
    );
    return true;
  }

  /** A system sheet was just rebuilt from the project — sort it as it was. */
  private resort(sheet: Sheet): void {
    if (sheet.sort) this.sortRows(sheet, sheet.sort.col, sheet.sort.descending);
  }

  /** Drag-reorder: move a run of rows or columns into an insertion slot. */
  moveBand(axis: 'row' | 'col', from: number, count: number, to: number): void {
    const sheet = this.sheet();
    if (to >= from && to <= from + count) return;
    this.pushUndo(axis === 'row' ? 'Move rows' : 'Move columns');
    moveBand(this.wb, sheet, axis, from, count, to);
    this.engine.rebuildAll();
    const landed = to > from ? to - count : to;
    if (axis === 'row') this.selectRows(landed, landed + count - 1);
    else this.selectCols(landed, landed + count - 1);
  }

  /**
   * Freeze everything up to and including `index`, so it stays on screen.
   *
   * "Up to" rather than "this one": freezing a header row means rows 1..n, and
   * asking for the count would make the user work out a number they can already
   * see by pointing at it.
   */
  freezeUpTo(axis: 'row' | 'col', index: number): void {
    const sheet = this.sheet();
    this.pushUndo(axis === 'row' ? 'Freeze rows' : 'Freeze columns');
    if (axis === 'row') sheet.freezeRows = Math.max(0, index);
    else sheet.freezeCols = Math.max(0, index);
    this.emit();
  }

  unfreeze(axis?: 'row' | 'col'): void {
    const sheet = this.sheet();
    this.pushUndo('Unfreeze');
    if (axis !== 'col') delete sheet.freezeRows;
    if (axis !== 'row') delete sheet.freezeCols;
    this.emit();
  }

  hideBand(axis: 'row' | 'col', from: number, to: number): void {
    const sheet = this.sheet();
    this.pushUndo(axis === 'row' ? 'Hide rows' : 'Hide columns');
    const map = axis === 'row' ? (sheet.hiddenRows ??= {}) : (sheet.hiddenCols ??= {});
    for (let i = Math.min(from, to); i <= Math.max(from, to); i++) map[i] = true;
    this.emit();
  }

  /**
   * Unhide whatever is hidden inside — or immediately beside — the selection.
   *
   * Beside matters: a hidden band has zero size, so it cannot be selected by
   * pointing at it. Selecting the rows either side of the gap is the only
   * gesture available, and it has to work.
   */
  showBand(axis: 'row' | 'col', from: number, to: number): void {
    const sheet = this.sheet();
    const map = axis === 'row' ? sheet.hiddenRows : sheet.hiddenCols;
    if (!map) return;
    this.pushUndo(axis === 'row' ? 'Unhide rows' : 'Unhide columns');
    for (let i = Math.min(from, to) - 1; i <= Math.max(from, to) + 1; i++) delete map[i];
    this.emit();
  }

  /** Anything hidden inside or beside the selection, so the menu can offer it. */
  hasHiddenNear(axis: 'row' | 'col', from: number, to: number): boolean {
    const sheet = this.sheet();
    const map = axis === 'row' ? sheet.hiddenRows : sheet.hiddenCols;
    if (!map) return false;
    for (let i = Math.min(from, to) - 1; i <= Math.max(from, to) + 1; i++) if (map[i]) return true;
    return false;
  }

  /** Set an explicit size across a whole band. */
  resizeBand(axis: 'row' | 'col', from: number, to: number, size: number): void {
    const sheet = this.sheet();
    this.pushUndo(axis === 'row' ? 'Resize rows' : 'Resize columns');
    for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
      if (axis === 'row') sheet.rowHeights[i] = Math.max(4, Math.round(size));
      else sheet.colWidths[i] = Math.max(24, Math.round(size));
    }
    this.emit();
  }

  /**
   * Set what long text does, and fit the rows when it is wrapping.
   *
   * The fit happens HERE, when wrap is switched on, rather than during
   * rendering: a height recomputed on every paint would fight a height the user
   * dragged, and the user's should win.
   */
  setOverflow(mode: 'overrun' | 'clip' | 'wrap'): void {
    this.applyStyle({ overflow: mode });
    if (mode !== 'wrap') return;
    const sheet = this.sheet();
    const { r1, c1, r2, c2 } = this.sel;
    for (let r = r1; r <= r2; r++) {
      let lines = 1;
      for (let c = c1; c <= c2; c++) {
        const cell = getCell(sheet, r, c);
        if (!cell) continue;
        const text = formatValue(this.engine.valueAt(sheet, r, c) ?? null, cell.fmt);
        lines = Math.max(lines, wrappedLines(text, colWidth(sheet, c) - 12, cell.s?.bold === true));
      }
      if (lines > 1) sheet.rowHeights[r] = Math.max(rowHeight(sheet, r), lines * 17 + 7);
    }
    this.emit();
  }

  /** Fit a row to its wrapped content, or a column to its widest cell. */
  autoSize(axis: 'row' | 'col', index: number): void {
    const sheet = this.sheet();
    if (axis === 'col') {
      let widest = 60;
      for (const [key, cell] of Object.entries(sheet.cells)) {
        const { row, col } = parseCk(key);
        if (col !== index) continue;
        const text = formatValue(this.engine.valueAt(sheet, row, col) ?? null, cell.fmt);
        widest = Math.max(widest, measureText(text, cell.s?.bold === true) + 14);
      }
      this.setColWidth(index, Math.min(widest, 600));
    }
  }

  // -- Sheets ---------------------------------------------------------------

  addSheet(): void {
    this.pushUndo('Add sheet');
    const sheet = addSheet(this.wb);
    this.wb.activeSheetId = sheet.id;
    this.sel = { sheetId: sheet.id, row: 1, col: 1, r1: 1, c1: 1, r2: 1, c2: 1 };
    // A1, ready to type on. Asked for explicitly, because focus is sitting on
    // the `+` button that was just clicked.
    this.focusGrid();
    this.emit();
  }

  activateSheet(id: string): void {
    if (!sheetById(this.wb, id)) return;
    this.wb.activeSheetId = id;
    this.sel = { sheetId: id, row: 1, col: 1, r1: 1, c1: 1, r2: 1, c2: 1 };
    // Deliberately does NOT end an open edit: clicking a sheet tab mid-formula
    // is how a cross-sheet reference gets picked — and focusGrid steps aside in
    // that case, since the editor must keep the caret.
    this.focusGrid();
    this.emit();
  }

  renameSheet(id: string, name: string): void {
    const sheet = sheetById(this.wb, id);
    // The Usages sheet is addressed by role, and formulas elsewhere reference
    // it by name — renaming it would break both for no gain.
    if (!sheet || isSystemSheet(sheet)) return;
    this.pushUndo('Rename sheet');
    sheet.name = uniqueSheetName(this.wb, name, id);
    // Formulas reference sheets by name, so every cross-sheet ref must re-resolve.
    this.engine.rebuildAll();
    this.emit();
  }

  /**
   * Move a sheet to an insertion SLOT — the gap between tabs, numbered 0..n, so
   * slot n means "after the last one". That is what the drop caret points at,
   * and it is a different number from an array index: dragging tab 0 to the gap
   * after tab 2 is slot 3, but index 2 once the tab has been lifted out.
   *
   * The system tabs (Features, Blocks, Usages, Analytics, Measures) form one
   * contiguous, pinned block at the end — an ordinary sheet cannot be dropped
   * into or past it, and a system tab cannot be dragged out of it. Reordering
   * WITHIN either region is still the user's business.
   */
  moveSheetToSlot(id: string, slot: number): void {
    const from = this.wb.sheets.findIndex((s) => s.id === id);
    if (from < 0) return;
    const moving = this.wb.sheets[from]!;
    const boundary = systemBlockStart(this.wb);
    const target = isSystemSheet(moving)
      ? clamp(slot, boundary, this.wb.sheets.length)
      : clamp(slot, 0, boundary);
    // Dropping into the gap on either side of where it already is changes
    // nothing, and should not land on the undo stack.
    if (target === from || target === from + 1) return;
    const to = target > from ? target - 1 : target;

    this.pushUndo('Reorder sheets');
    const next = [...this.wb.sheets];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    this.wb.sheets = next;
    this.emit();
  }

  deleteSheet(id: string): void {
    const target = sheetById(this.wb, id);
    // Deleting the Usages sheet would remove the only place usages are edited,
    // and the next load would put it straight back.
    if (!target || isSystemSheet(target)) return;
    this.pushUndo('Delete sheet');
    const index = this.wb.sheets.indexOf(target);
    this.wb.sheets = this.wb.sheets.filter((s) => s.id !== id);
    for (const [bid, b] of Object.entries(this.wb.blocks)) if (b.sheetId === id) delete this.wb.blocks[bid];
    for (const [cid, c] of Object.entries(this.wb.charts)) if (c.sheetId === id) delete this.wb.charts[cid];
    if (this.wb.namedRanges) {
      for (const [name, r] of Object.entries(this.wb.namedRanges)) if (r.sheetId === id) delete this.wb.namedRanges[name];
    }
    // The last ordinary sheet can go too — a fresh empty one takes its place,
    // because a workbook of nothing but the project's own sheets has nowhere
    // to type.
    const users = this.wb.sheets.filter((s) => !isSystemSheet(s));
    if (users.length === 0) addSheet(this.wb, 'Sheet1');
    // The neighbour, not the first tab: deleting one in the middle should not
    // throw you back to the start.
    const remaining = this.wb.sheets.filter((s) => !isSystemSheet(s));
    const next = remaining.find((s) => this.wb.sheets.indexOf(s) >= index) ?? remaining[remaining.length - 1]!;
    this.wb.activeSheetId = next.id;
    this.sel = { sheetId: next.id, row: 1, col: 1, r1: 1, c1: 1, r2: 1, c2: 1 };
    this.engine.rebuildAll();
    this.emit();
    // No confirm dialog — inside Giraffe's iframe one can be blocked outright,
    // which made × do nothing. Undo is the safety net, so say it exists.
    this.setWriteStatus({ ok: true, detail: `Deleted "${target.name}". Cmd/Ctrl+Z brings it back.` });
  }

  // -- Warnings -------------------------------------------------------------

  /**
   * The warning standing on a cell. "Warn but allow" only works if the warning
   * is visible, so it reaches the user as an amber underline and a tooltip.
   */
  cellWarning(sheetId: string, row: number, col: number): string | null {
    if (!this.showWarnings) return null;
    return this.engine.diagnosticsAt(sheetId, row, col)[0]?.message ?? null;
  }

  /**
   * Whether the amber underlines are on. OFF by default.
   *
   * A warning that marks up the sheet before anyone asked is noise on a model
   * that is working: the count in the status bar is enough to say something is
   * worth a look, and the marks appear when the user goes looking.
   */
  showWarnings = false;

  toggleWarnings(): void {
    this.showWarnings = !this.showWarnings;
    this.emit();
  }

  /** Every standing warning, for the status chip. */
  warnings(): { message: string; field: string }[] {
    return this.engine.diagnostics().map((d) => ({ message: d.message, field: d.field }));
  }

  /**
   * Stamp a schedule at a cell as ORDINARY FORMULAS. Nothing is bound
   * afterwards — this is a one-time write like any other edit.
   */
  stampSchedule(anchor: { row: number; col: number }, spec: ScheduleSpec): void {
    const sheet = this.sheet();
    this.pushUndo('Insert schedule');
    const rect = stampSchedule(sheet, this.engine, anchor, spec, this.data());
    this.selectRange(rect.r1, rect.c1, rect.r2, rect.c2);
  }

  // -- Databases -------------------------------------------------------------

  /**
   * The database wizard, when open. `dbId` is the database being edited; null
   * is a new one, placed at the selection.
   */
  dbWizard: { dbId: string | null } | null = null;

  openDbWizard(dbId: string | null = null): void {
    this.dbWizard = { dbId };
    this.emitUiOnly();
  }

  closeDbWizard(): void {
    this.dbWizard = null;
    this.emitUiOnly();
  }

  database(id: string): Database | undefined {
    return this.sheet().databases?.[id];
  }

  /** The database row cell under a point, for the grid's ▾ and its menu. */
  dbCellAt(row: number, col: number): { db: Database; index: number } | null {
    return databaseAt(this.sheet(), row, col);
  }

  /** Place a new database at the selection, or rewrite an existing one. */
  // -- The sheet filter ------------------------------------------------------

  /**
   * The Filter button. The bar shows exactly when the sheet has a filter, so
   * the button adds one — starting with what people filter by, the layer and
   * the usage, both on "any" — or takes it off.
   */
  toggleFilterBar(): void {
    const sheet = this.sheet();
    if (sheet.sheetFilter) {
      this.setSheetFilter(undefined);
      return;
    }
    const fields = this.data().fields('bakedSections');
    const start = ['layerId', 'usage'].filter((f) => fields.includes(f)).map((field) => ({ field, values: [] }));
    this.setSheetFilter(start.length > 0 ? start : [{ field: fields[0] ?? '', values: [] }]);
  }

  /**
   * Replace the sheet's filter. Every GI formula on it is volatile, so a data
   * pass recomputes them against the new filter; databases' sub-rows read it
   * on the next draw.
   */
  setSheetFilter(filter: SheetFilterClause[] | undefined): void {
    const sheet = this.sheet();
    this.pushUndo('Sheet filter');
    if (filter) sheet.sheetFilter = filter;
    else delete sheet.sheetFilter;
    this.engine.onDataChanged();
    this.emit();
  }

  /** The current sheet's filter as text, for a source — what its GI calls add. */
  sheetFilterFor(sheet: Sheet, source: SourceKind): string {
    // With the layer panel, as GI.* formulas on the same sheet do — or a
    // "Visible only" filter would scope the formulas and not the database.
    const d = this.data();
    return sheetFilterText(sheet, d.fields(source), layerVisibility(d.visibleDrawingLayers?.bind(d)));
  }

  /** The extra filter a database's sub-rows apply: its sheet's. */
  dbFilterText(db: Database): string {
    const sheet = this.wb.sheets.find((s) => s.databases?.[db.id]) ?? this.sheet();
    return this.sheetFilterFor(sheet, db.source);
  }

  saveDatabase(query: Pick<Database, 'source' | 'criteria' | 'columns' | 'groupBy' | 'thenBy'>, dbId: string | null): void {
    const sheet = this.sheet();
    const existing = dbId ? sheet.databases?.[dbId] : undefined;
    this.pushUndo(existing ? 'Edit database' : 'Insert database');
    const db: Database = existing
      ? { ...existing, ...query }
      : // Always column A: a database is its own table beside the sheet, not
        // something dropped wherever the cursor happened to be.
        { id: newId('db'), anchor: { row: this.sel.row, col: 1 }, width: 0, ...query };
    if (!existing && this.rowIsBusy(db.anchor.row, query.columns.length)) {
      // Never over somebody's cells. A fresh row goes in, and because it goes
      // in through insertBand every formula below follows the rows it names.
      insertBand(this.wb, sheet, 'row', db.anchor.row, 1);
      this.engine.rebuildAll();
    }
    const written = writeDatabase(sheet, this.engine, db, this.data(), !existing);
    this.focusDatabase(written);
  }

  /** A database whose row the selection covers — what the right-click menu offers to act on. */
  databaseInSelection(): Database | null {
    const { r1, r2 } = this.sel;
    for (const db of Object.values(this.sheet().databases ?? {})) {
      if (db.anchor.row >= r1 && db.anchor.row <= r2) return db;
    }
    return null;
  }

  /** Something on this row where a new database's columns would go. */
  rowIsBusy(row: number, width: number): boolean {
    const sheet = this.sheet();
    for (let c = 1; c <= Math.max(1, width); c++) if (sheet.cells[`${row},${c}`] !== undefined) return true;
    return databaseAt(sheet, row, 1) !== null;
  }

  /** One change from a title's menu: regroup, re-aggregate, drop a column. */
  updateDatabase(dbId: string, patch: Partial<Pick<Database, 'columns' | 'groupBy' | 'thenBy' | 'collapsed'>>, label: string): void {
    const sheet = this.sheet();
    const db = sheet.databases?.[dbId];
    if (!db) return;
    this.pushUndo(label);
    writeDatabase(sheet, this.engine, { ...db, ...patch }, this.data());
    this.emit();
  }

  deleteDatabase(dbId: string): void {
    const sheet = this.sheet();
    const db = sheet.databases?.[dbId];
    if (!db) return;
    this.pushUndo('Delete database');
    removeDatabase(sheet, this.engine, db);
    this.emit();
    this.setWriteStatus({ ok: true, detail: 'Deleted the database. Cmd/Ctrl+Z brings it back.' });
  }

  /**
   * The objects behind a database sub-row — one object, or a group's members —
   * or, with `sub` null, everything the database matched. As feature ids, once
   * each: a block stamped down a line is many rows under one id.
   */
  dbObjectIds(dbId: string, sub: number | null): string[] {
    const db = this.database(dbId);
    if (!db) return [];
    const data = this.data();
    const extra = this.dbFilterText(db);
    const rows = sub === null ? matchesOf(db, data, extra).rows : subRowMembers(db, data, extra)[sub]?.members ?? [];
    return [...new Set(rows.map((r) => r.id).filter((id) => id !== ''))];
  }

  async selectDbObjects(dbId: string, sub: number | null): Promise<void> {
    const ids = this.dbObjectIds(dbId, sub);
    if (ids.length === 0) {
      this.setWriteStatus({ ok: false, detail: 'Nothing on the map for that row.' });
      return;
    }
    try {
      await setSelectedFeatures(ids);
      this.setWriteStatus({ ok: true, detail: `Selected ${ids.length} on the map.` });
    } catch (err) {
      this.setWriteStatus({ ok: false, detail: `Could not select on the map: ${err instanceof Error ? err.message : String(err)}` });
    }
  }

  async zoomToDbObjects(dbId: string, sub: number | null): Promise<void> {
    await this.zoomToIds(this.dbObjectIds(dbId, sub));
  }

  private focusDatabase(db: Database): void {
    const sheet = this.sheet();
    const c2 = db.anchor.col + db.width - 1;
    this.sel = {
      sheetId: sheet.id,
      row: db.anchor.row,
      col: db.anchor.col,
      r1: db.anchor.row,
      c1: db.anchor.col,
      r2: db.anchor.row,
      c2: clamp(c2, 1, sheet.cols),
    };
    this.emit();
  }

  // -- Charts ----------------------------------------------------------------

  upsertChart(chart: ChartSpec): void {
    this.pushUndo('Chart');
    this.wb.charts[chart.id] = chart;
    this.emit();
  }

  /** Move/resize during a drag — no undo frame per pointermove. */
  patchChartLive(id: string, patch: Partial<ChartSpec>): void {
    const chart = this.wb.charts[id];
    if (!chart) return;
    this.wb.charts[id] = { ...chart, ...patch };
    this.emit();
  }

  removeChart(id: string): void {
    if (!this.wb.charts[id]) return;
    this.pushUndo('Remove chart');
    delete this.wb.charts[id];
    this.emit();
  }

  /** Replace the whole document (hydrate from persistence, or New). */
  /**
   * Replace the whole workbook.
   *
   * `origin` matters because hydration is ASYNCHRONOUS and lands twice — this
   * browser's copy first, the project's a second or two later. A user who
   * opened an example in that window had the old workbook dropped back on top
   * of it, which is where "opening an example brought back my old edits" came
   * from. The counter lets a late restore see that it has been overtaken.
   */
  userLoads = 0;

  loadWorkbook(wb: Workbook, origin: 'user' | 'restore' = 'user'): void {
    if (origin === 'user') this.userLoads += 1;
    this.wb = wb;
    // BEFORE ensureSystemSheets, which writes the Show column from this set.
    this.hiddenFeatures = new Set(wb.hiddenFeatures ?? []);
    if (!sheetById(this.wb, this.wb.activeSheetId)) this.wb.activeSheetId = this.wb.sheets[0]!.id;
    this.engine.setWorkbook(this.wb);
    this.ensureSystemSheets();
    this.engine.onDataChanged();
    this.sel = { sheetId: this.wb.activeSheetId, row: 1, col: 1, r1: 1, c1: 1, r2: 1, c2: 1 };
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.emit();
  }

  /**
   * Load a workbook read from an .xlsx, then repair the formulas that will not
   * run here.
   *
   * The engine is what decides which formulas survive: `repairImportedFormulas`
   * asks it what each imported formula actually evaluates to, and puts Excel's
   * own number into the ones that come out as errors.
   *
   * Returns how many were demoted, for the import report.
   */
  loadImported(imported: XlsxImport): number {
    this.loadWorkbook(imported.workbook);
    const demoted = repairImportedFormulas(this.wb, imported.cached, {
      valueAt: (sheetId, row, col) => this.engine.valueOf(sheetId, row, col),
      evaluate: (sheetName, formula, row, col) => this.engine.evaluateFormula(formula, sheetName, row, col),
      // A whole rebuild rather than a recalculation per cell: the repaired
      // cells are scattered across every sheet, and anything reading one has to
      // be re-run anyway.
      recalculate: () => this.engine.setWorkbook(this.wb),
    });
    if (demoted > 0) this.emit();
    return demoted;
  }

  /**
   * Add an imported .xlsx's sheets into the OPEN workbook, as the alternative
   * to `loadImported` replacing it outright.
   *
   * Sheet ids never collide with the open workbook's — `newId()` is a single
   * monotonic counter for the whole page session — only NAMES can, so each
   * sheet goes through the same `uniqueSheetName` every other new sheet uses.
   * `repairImportedFormulas` is the same repair pass `loadImported` runs;
   * cached values key on `sheetId`, which is untouched by the rename.
   *
   * Returns how many formulas were demoted, for the import report.
   */
  addImportedSheets(imported: XlsxImport): number {
    this.pushUndo('Import sheets');
    // A sheet renamed to dodge a clash takes the imported formulas that name
    // it along, or they would read the open workbook's sheet of the old name.
    const renames = new Map<string, string>();
    for (const sheet of imported.workbook.sheets) {
      const name = uniqueSheetName(this.wb, sheet.name);
      if (name !== sheet.name) renames.set(sheet.name.toLowerCase(), name);
      sheet.name = name;
      this.wb.sheets.splice(systemBlockStart(this.wb), 0, sheet);
    }
    if (renames.size > 0) {
      for (const sheet of imported.workbook.sheets) {
        for (const cell of Object.values(sheet.cells)) {
          if (cell.f) cell.f = renameSheetRefs(cell.f, renames);
        }
      }
    }
    // Keyed by sheetId, not name, so the rename above does not disturb them.
    // An existing name wins on collision — "add" means alongside what is
    // already here, not overwriting it.
    if (imported.workbook.namedRanges) {
      this.wb.namedRanges = { ...imported.workbook.namedRanges, ...this.wb.namedRanges };
    }
    const firstId = imported.workbook.sheets[0]!.id;
    this.wb.activeSheetId = firstId;
    this.sel = { sheetId: firstId, row: 1, col: 1, r1: 1, c1: 1, r2: 1, c2: 1 };
    // Full rebuild, not incremental: the new sheets' formulas have never been
    // evaluated here, and `repairImportedFormulas` needs real values to probe.
    this.engine.setWorkbook(this.wb);
    const demoted = repairImportedFormulas(this.wb, imported.cached, {
      valueAt: (sheetId, row, col) => this.engine.valueOf(sheetId, row, col),
      evaluate: (sheetName, formula, row, col) => this.engine.evaluateFormula(formula, sheetName, row, col),
      recalculate: () => this.engine.setWorkbook(this.wb),
    });
    this.emit();
    return demoted;
  }

  newWorkbook(): Workbook {
    return createWorkbook();
  }

  newBlockId(): string {
    return newId('blk');
  }

  newChartId(): string {
    return newId('cht');
  }
}

/** Positive modulo — `-1 % 3` is -1 in JS, and a tiling index must not be. */
function mod(a: number, n: number): number {
  return ((a % n) + n) % n;
}

/** How long a write-status chip stays. Matches the fade in styles.css. */
const WRITE_STATUS_MS = 10_000;

const EMPTY_OVERRIDES: Readonly<Record<string, MeasureClass>> = Object.freeze({});

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export const store = new AppStore();


/** Two id sets holding the same ids — the test that keeps the rpc count down. */
function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}
